import axios, { AxiosInstance, AxiosError } from 'axios';
import type {
  AuthResponse,
  LoginRequest,
  RegisterRequest,
  User,
  UserUpdateRequest,
  ChangePasswordRequest,
  DeleteAccountRequest,
  UserPreferences,
  UserPreferencesUpdate,
  // UserApiKeys and UserApiKeysUpdate removed - API keys now in .env
  MessageResponse,
  CompanyProfile,
  CompanyService,
  CompanyResearchResponse,
  OnboardingRequest,
  OnboardingStatus,
  GenerateRequest,
  GenerationResponse,
  TonePreset,
  Principle,
  Collection,
  SavedPost,
  PaginatedPosts,
  PostSelectRequest,
  PostSelectResponse,
  SubscriptionInfo,
  SocialPlatform,
  ConnectedSocialAccount,
  SocialPostResult,
  UserSocialKeys,
  UserSocialKeysUpdate,
  Fact,
  MineCandidate,
} from '@/types';
import {
  DEV_MODE,
  MOCK_USER,
  MOCK_COMPANY_PROFILE,
  MOCK_SERVICES,
} from '@/lib/devMode';
import {
  DEV_GEN_MS_PER_PLATFORM,
  DEV_KEYS,
  DEV_PSYCH_SELECTION,
  buildDevCampaignCalendar,
  buildDevCaseStudies,
  buildDevCaseStudyDetail,
  buildDevExamples,
  buildDevFeed,
  buildDevGeneratedPlatform,
  buildDevPendingAutopilot,
  buildDevDisruptions,
  buildDevSessionDetail,
  buildFreshDisruption,
  collectionsWithCounts,
  deriveDashboardStats,
  devDelay,
  devFault,
  devRead,
  devWrite,
} from '@/lib/devFixtures';

// API Base URL Configuration
// - Development: Uses Vite proxy (/api/v1) via env variable
// - Production: Dynamically determined based on current domain
const getApiBaseUrl = (): string => {
  // Explicit override: dev (Vite proxy) or a deployment that talks to a remote backend.
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }

  // Production: hosts that intentionally route to a DIFFERENT backend origin.
  const hostname = window.location.hostname;
  const domainApiMap: Record<string, string> = {
    'zupapost.ai': 'https://zupapost.ai/api/v1',
    'www.zupapost.ai': 'https://zupapost.ai/api/v1',
  };
  if (domainApiMap[hostname]) {
    return domainApiMap[hostname];
  }

  // Default: same-origin. Every deployment (incl. logistics.zupapost.ai) serves
  // /api/v1 behind its own reverse proxy, so a relative base needs no CORS and
  // never depends on a build-time domain var being set correctly.
  return '/api/v1';
};

const API_BASE_URL = getApiBaseUrl();

/**
 * Fix logo/upload URLs that were stored with localhost/127.0.0.1 in the DB.
 * Rewrites them to use the current origin so they work on any deployment.
 */
function fixUploadUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  // Match http(s)://127.0.0.1:PORT or http(s)://localhost:PORT
  const localhostPattern = /^https?:\/\/(?:127\.0\.0\.1|localhost)(?::\d+)?/;
  if (localhostPattern.test(url)) {
    return url.replace(localhostPattern, window.location.origin);
  }
  return url;
}

/**
 * Single-source hashtag string formatter — strips any leading # a tag may
 * already carry, prepends exactly one #, and joins with a single space.
 */
export function formatHashtags(tags: string[] | undefined | null): string {
  return (tags || []).map(t => '#' + String(t).replace(/^#+/, '')).join(' ');
}

// Simulate network delay in dev mode. Routed through devDelay so
// `window.__zupaDev.speed('fast')` can collapse every dev delay at once.
const delay = (ms: number) => devDelay(ms);

/** DEV_MODE only — the seeded posts, read fresh so mutations are visible. */
const devPosts = (): SavedPost[] => devRead<SavedPost[]>(DEV_KEYS.posts, []);
const saveDevPosts = (posts: SavedPost[]) => devWrite(DEV_KEYS.posts, posts);

/**
 * DEV_MODE only — local storage keys for the Sprint 25 bank surfaces. Not in
 * DEV_KEYS (devFixtures.ts is outside this piece's scope) — plain string keys
 * work the same with the existing devRead/devWrite helpers.
 */
const DEV_FUEL_KEY = 'dev_ws_fuel';
const DEV_HARVEST_PENDING_KEY = 'dev_ws_harvest_pending';
const DEV_EMPTY_FUEL: import('@/types').FuelGauge = {
  number: 0, client_win: 0, credential: 0, capability: 0,
  verbatim: 0, fact_total: 0, stance: 0, boundary: 0,
  moment_fresh: 0, moment_total: 0,
};

/** DEV_MODE only — sample Content Ideas so the feature is visible without a backend. */
const DEV_CONTENT_IDEAS: import('@/types').ContentIdea[] = [
  {
    id: 'idea-dev-1',
    headline: 'Why most design systems die in year two',
    description: 'The adoption cliff nobody plans for — and the three habits that get a system past it.',
    source_context: 'Recurring theme in your writing samples',
    structured_prompt: 'Write about why most design systems fail in their second year, and what teams that survive do differently.',
    tags: ['design ops', 'opinion'],
  },
  {
    id: 'idea-dev-2',
    headline: 'Tokens-as-code: what broke when we shipped it',
    description: 'A candid retro of the migration — what we got wrong, and what we would do again.',
    source_context: 'From your Facts & Achievements',
    structured_prompt: 'Write a candid retrospective about shipping tokens-as-code: what broke, what we fixed, what we would repeat.',
    tags: ['engineering', 'story'],
  },
  {
    id: 'idea-dev-3',
    headline: 'Stop hand-syncing colours',
    description: 'Define them once, consume them everywhere. The whole pitch in one post.',
    source_context: 'Matches your brand voice',
    structured_prompt: 'Write a short, punchy post arguing that brand colours should be defined once and consumed everywhere.',
    tags: ['design systems'],
  },
];

// Default preferences for dev mode
const DEFAULT_PREFERENCES: UserPreferences = {
  default_tone: null,
  default_platforms: [],
  email_notifications: true,
  marketing_emails: false,
  enter_to_send: false, // Default: Enter adds new line, click button to send
  has_completed_tutorial: false, // Guided tour: unseen by default so it can fire
  tutorial_dismissed_at: null,
};

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      // Axios defaults to `timeout: 0` — NO timeout — so before this line every call in the
      // app could hang forever on a stalled socket, and the UI had no way to tell a slow
      // request from a dead one. That is the shared amplifier underneath the "spinner that
      // never resolves" reports: the button had nothing to fail on.
      // 120s is deliberately generous (the longest real calls are generation and the
      // case-study engine) but finite — and Cloudflare severs the connection at ~100s
      // anyway, so nothing legitimate survives past this. Per-call overrides still win.
      timeout: 120_000,
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    // Add auth token to requests
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('access_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Handle auth errors (only in production mode)
    // Only redirect on 401 if session expired on a protected route
    // Don't redirect if already on auth pages (login, register, etc.)
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (!DEV_MODE && error.response?.status === 401) {
          // Check if we're on an auth page (by browser URL, not request URL)
          const currentPath = window.location.pathname;
          const isOnAuthPage = currentPath.includes('/login') ||
                               currentPath.includes('/register') ||
                               currentPath.includes('/verify') ||
                               currentPath.includes('/forgot') ||
                               currentPath.includes('/reset');
          
          // Only redirect if NOT on an auth page AND user was previously logged in
          if (!isOnAuthPage && localStorage.getItem('access_token')) {
            localStorage.removeItem('access_token');
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // ============================================
  // Auth endpoints
  // ============================================

  async register(data: RegisterRequest): Promise<MessageResponse> {
    if (DEV_MODE) {
      await delay(500);
      return { message: 'Registration successful! Please check your email to verify your account.' };
    }
    const response = await this.client.post('/auth/register', data);
    return response.data;
  }

  async login(data: LoginRequest): Promise<AuthResponse> {
    if (DEV_MODE) {
      await delay(500);
      const token = 'dev-token-' + Date.now();
      localStorage.setItem('access_token', token);
      return {
        access_token: token,
        token_type: 'bearer',
        user: { ...MOCK_USER, email: data.email } as User,
      };
    }
    const response = await this.client.post('/auth/login', data);
    if (response.data.access_token) {
      localStorage.setItem('access_token', response.data.access_token);
    }
    return response.data;
  }

  async googleAuthUrl(state?: string): Promise<{ authorization_url: string }> {
    if (DEV_MODE) {
      return { authorization_url: '/login?google=mock' };
    }
    const response = await this.client.get('/auth/google/url', {
      params: { state },
    });
    return response.data;
  }

  async googleCallback(code: string, remember_me?: boolean): Promise<AuthResponse> {
    if (DEV_MODE) {
      await delay(500);
      const token = 'dev-google-token-' + Date.now();
      localStorage.setItem('access_token', token);
      return {
        access_token: token,
        token_type: 'bearer',
        user: MOCK_USER as User,
      };
    }
    const response = await this.client.post('/auth/google/callback', {
      code,
      remember_me,
    });
    if (response.data.access_token) {
      localStorage.setItem('access_token', response.data.access_token);
    }
    return response.data;
  }

  async consentStatus(): Promise<{ tos_accepted: boolean; ai_disclosure_accepted: boolean }> {
    const { data } = await this.client.get('/auth/consent-status');
    return data;
  }

  async acceptTerms(): Promise<void> {
    await this.client.post('/auth/accept-terms');
  }

  async acceptAIDisclosure(): Promise<void> {
    await this.client.post('/auth/ai-disclosure-accept');
  }

  async verifyEmail(token: string): Promise<MessageResponse> {
    if (DEV_MODE) {
      await delay(300);
      return { message: 'Email verified successfully!' };
    }
    const response = await this.client.post('/auth/verify-email', { token });
    return response.data;
  }

  async resendVerification(email: string): Promise<MessageResponse> {
    if (DEV_MODE) {
      await delay(300);
      return { message: 'Verification email sent!' };
    }
    const response = await this.client.post('/auth/resend-verification', { email });
    return response.data;
  }

  async forgotPassword(email: string): Promise<MessageResponse> {
    if (DEV_MODE) {
      await delay(300);
      return { message: 'Password reset email sent!' };
    }
    const response = await this.client.post('/auth/forgot-password', { email });
    return response.data;
  }

  async resetPassword(token: string, new_password: string): Promise<MessageResponse> {
    if (DEV_MODE) {
      await delay(300);
      return { message: 'Password reset successfully!' };
    }
    const response = await this.client.post('/auth/reset-password', {
      token,
      new_password,
    });
    return response.data;
  }

  async logout(): Promise<MessageResponse> {
    if (DEV_MODE) {
      localStorage.removeItem('access_token');
      return { message: 'Logged out successfully' };
    }
    const response = await this.client.post('/auth/logout');
    localStorage.removeItem('access_token');
    return response.data;
  }

  async getCurrentUser(): Promise<User> {
    if (DEV_MODE) {
      await delay(200);
      const token = localStorage.getItem('access_token');
      if (!token) throw new Error('No token');
      
      // Get stored user data or use mock
      const storedUser = localStorage.getItem('dev_user');
      return storedUser ? JSON.parse(storedUser) : MOCK_USER as User;
    }
    const response = await this.client.get('/auth/me');
    return response.data;
  }

  // ============================================
  // User Profile endpoints (NEW)
  // ============================================

  async updateProfile(data: UserUpdateRequest): Promise<User> {
    if (DEV_MODE) {
      await delay(300);
      const storedUser = localStorage.getItem('dev_user');
      const user = storedUser ? JSON.parse(storedUser) : { ...MOCK_USER };
      
      if (data.full_name !== undefined) user.full_name = data.full_name;
      if (data.avatar_url !== undefined) user.avatar_url = data.avatar_url;
      
      localStorage.setItem('dev_user', JSON.stringify(user));
      return user;
    }
    const response = await this.client.patch('/auth/me', data);
    return response.data;
  }

  async changePassword(data: ChangePasswordRequest): Promise<MessageResponse> {
    if (DEV_MODE) {
      await delay(300);
      // Simulate validation
      if (data.current_password === 'wrong') {
        throw { response: { data: { detail: 'Current password is incorrect' } } };
      }
      return { message: 'Password changed successfully.' };
    }
    const response = await this.client.post('/auth/change-password', data);
    return response.data;
  }

  async deleteAccount(data: DeleteAccountRequest): Promise<MessageResponse> {
    if (DEV_MODE) {
      await delay(500);
      if (!data.confirm) {
        throw { response: { data: { detail: 'Please confirm account deletion' } } };
      }
      // Clear all dev mode data
      localStorage.removeItem('access_token');
      localStorage.removeItem('dev_user');
      localStorage.removeItem('dev_onboarding_completed');
      localStorage.removeItem('dev_company_profile');
      localStorage.removeItem('dev_services');
      localStorage.removeItem('dev_preferences');
      return { message: 'Account deleted successfully.' };
    }
    const response = await this.client.delete('/auth/me', { data });
    return response.data;
  }

  // ============================================
  // User Preferences endpoints (NEW)
  // ============================================

  async getPreferences(): Promise<UserPreferences> {
    if (DEV_MODE) {
      await delay(200);
      const stored = localStorage.getItem('dev_preferences');
      return stored ? JSON.parse(stored) : DEFAULT_PREFERENCES;
    }
    const response = await this.client.get('/auth/preferences');
    return response.data;
  }

  async updatePreferences(data: UserPreferencesUpdate): Promise<UserPreferences> {
    if (DEV_MODE) {
      await delay(300);
      const stored = localStorage.getItem('dev_preferences');
      const current = stored ? JSON.parse(stored) : { ...DEFAULT_PREFERENCES };
      
      // Merge updates
      const updated = { ...current };
      if (data.default_tone !== undefined) updated.default_tone = data.default_tone;
      if (data.default_platforms !== undefined) updated.default_platforms = data.default_platforms;
      if (data.email_notifications !== undefined) updated.email_notifications = data.email_notifications;
      if (data.marketing_emails !== undefined) updated.marketing_emails = data.marketing_emails;
      if (data.enter_to_send !== undefined) updated.enter_to_send = data.enter_to_send;
      // The guided-tour flags. These were MISSING from the dev merge, so
      // endTour(true) appeared to do nothing in dev: the tutorial could never be
      // marked complete and would replay forever. Production always persisted them
      // (real PATCH, real columns) — the mock was silently dropping them, which is
      // exactly what makes a bug like this impossible to see while testing.
      // `tutorial_dismissed_at` accepts an explicit null (that's how "replay the
      // tour" clears it), so check for `undefined`, not falsiness.
      if (data.has_completed_tutorial !== undefined) updated.has_completed_tutorial = data.has_completed_tutorial;
      if (data.tutorial_dismissed_at !== undefined) updated.tutorial_dismissed_at = data.tutorial_dismissed_at;

      localStorage.setItem('dev_preferences', JSON.stringify(updated));
      return updated;
    }
    const response = await this.client.patch('/auth/preferences', data);
    return response.data;
  }

  // ============================================
  // Company endpoints
  // ============================================

  async researchCompany(website_url: string, content_url?: string, manual_description?: string): Promise<CompanyResearchResponse> {
    if (DEV_MODE) {
      // 9s, not 2s. The real call goes and reads an entire website with an LLM and
      // takes far longer than two seconds — so a 2s mock made the analyzing state
      // flash past and left it effectively untestable in dev. A mock that's much
      // faster than reality hides exactly the experience it exists to exercise.
      await delay(9000);
      // Extract domain name from URL for mock company name
      let domain = website_url.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
      const companyName = domain.split('.')[0].charAt(0).toUpperCase() + domain.split('.')[0].slice(1);

      return {
        name: companyName,
        website_url: website_url,
        industry: 'Technology',
        description: `${companyName} is a leading provider of innovative solutions in the technology space.`,
        target_audience: 'Small to medium businesses seeking digital transformation',
        brand_voice: 'Professional, innovative, customer-focused',
        suggested_services: [
          { name: 'Consulting', description: 'Expert advice and strategy' },
          { name: 'Development', description: 'Custom software solutions' },
        ],
        raw_research: { source: 'mock', url: website_url },
        brand_colors: ['#4F46E5', '#10B981', '#F59E0B'],
        primary_color: '#4F46E5',
        visual_style: 'Modern & Clean',
        visual_mood: 'Professional, Trustworthy',
        photography_style: 'Corporate lifestyle imagery',
        logo_description: `${companyName} logo with modern typography`,
        extracted_logo_url: null,
        brand_fonts: ['Inter', 'Roboto'],
      };
    }
    const response = await this.client.post('/company/research', {
      website_url,
      ...(content_url ? { content_url } : {}),
      ...(manual_description ? { manual_description } : {}),
    });
    return response.data;
  }

  async getOnboardingStatus(): Promise<OnboardingStatus> {
    if (DEV_MODE) {
      await delay(200);
      // Check if user has completed onboarding (stored in localStorage for dev mode)
      const completed = localStorage.getItem('dev_onboarding_completed');
      return {
        is_onboarded: completed === 'true',
        has_profile: completed === 'true',
        services_count: completed === 'true' ? MOCK_SERVICES.length : 0,
      };
    }
    const response = await this.client.get('/company/onboarding/status');
    return response.data;
  }

  async completeOnboarding(data: OnboardingRequest): Promise<{
    profile: CompanyProfile;
    services: CompanyService[];
  }> {
    if (DEV_MODE) {
      await delay(1000);
      // Mark onboarding as completed
      localStorage.setItem('dev_onboarding_completed', 'true');
      
      const profile: CompanyProfile = {
        id: 'dev-company-' + Date.now(),
        name: data.name,
        website_url: data.website_url,
        industry: data.industry,
        description: data.description,
        target_audience: data.target_audience,
        brand_voice: data.brand_voice,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      const services: CompanyService[] = (data.services || []).map((s, i) => ({
        id: `service-${i}`,
        name: s.name,
        description: s.description,
        display_order: i,
        is_active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }));

      // Store in localStorage for dev mode
      localStorage.setItem('dev_company_profile', JSON.stringify(profile));
      localStorage.setItem('dev_services', JSON.stringify(services));

      return { profile, services };
    }
    const response = await this.client.post('/company/onboarding/complete', data);
    return response.data;
  }

  async skipOnboarding(): Promise<OnboardingStatus> {
    if (DEV_MODE) {
      await delay(300);
      // Mark onboarding as completed
      localStorage.setItem('dev_onboarding_completed', 'true');
      return {
        is_onboarded: true,
        has_profile: true,
        services_count: 0,
      };
    }
    const response = await this.client.post('/company/onboarding/skip');
    return response.data;
  }

  async getCompanyProfile(): Promise<{
    profile: CompanyProfile;
    services: CompanyService[];
  } | null> {
    if (DEV_MODE) {
      await delay(200);
      const profileStr = localStorage.getItem('dev_company_profile');
      const servicesStr = localStorage.getItem('dev_services');
      
      if (profileStr) {
        return {
          profile: JSON.parse(profileStr),
          services: servicesStr ? JSON.parse(servicesStr) : MOCK_SERVICES,
        };
      }
      return null;
    }
    const response = await this.client.get('/company/profile');
    return response.data;
  }

  async updateCompanyProfile(data: Partial<CompanyProfile>): Promise<CompanyProfile> {
    if (DEV_MODE) {
      await delay(300);
      const profileStr = localStorage.getItem('dev_company_profile');
      const profile = profileStr ? JSON.parse(profileStr) : MOCK_COMPANY_PROFILE;
      const updated = { ...profile, ...data, updated_at: new Date().toISOString() };
      localStorage.setItem('dev_company_profile', JSON.stringify(updated));
      return updated;
    }
    const response = await this.client.put('/company/profile', data);
    return response.data;
  }

  async getServices(): Promise<CompanyService[]> {
    if (DEV_MODE) {
      await delay(200);
      const servicesStr = localStorage.getItem('dev_services');
      return servicesStr ? JSON.parse(servicesStr) : MOCK_SERVICES;
    }
    const response = await this.client.get('/company/services');
    return response.data;
  }

  async createService(name: string, description?: string): Promise<CompanyService> {
    if (DEV_MODE) {
      await delay(300);
      const servicesStr = localStorage.getItem('dev_services');
      const services: CompanyService[] = servicesStr ? JSON.parse(servicesStr) : [];
      
      const newService: CompanyService = {
        id: `service-${Date.now()}`,
        name,
        description,
        display_order: services.length,
        is_active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      
      services.push(newService);
      localStorage.setItem('dev_services', JSON.stringify(services));
      return newService;
    }
    const response = await this.client.post('/company/services', {
      name,
      description,
    });
    return response.data;
  }

  async updateService(id: string, name: string, description?: string): Promise<CompanyService> {
    if (DEV_MODE) {
      await delay(300);
      const servicesStr = localStorage.getItem('dev_services');
      const services: CompanyService[] = servicesStr ? JSON.parse(servicesStr) : [];
      const index = services.findIndex(s => s.id === id);
      if (index !== -1) {
        services[index] = { ...services[index], name, description, updated_at: new Date().toISOString() };
        localStorage.setItem('dev_services', JSON.stringify(services));
        return services[index];
      }
      throw new Error('Service not found');
    }
    const response = await this.client.put(`/company/services/${id}`, {
      name,
      description,
    });
    return response.data;
  }

  async deleteService(id: string): Promise<MessageResponse> {
    if (DEV_MODE) {
      await delay(300);
      const servicesStr = localStorage.getItem('dev_services');
      const services: CompanyService[] = servicesStr ? JSON.parse(servicesStr) : [];
      const filtered = services.filter(s => s.id !== id);
      localStorage.setItem('dev_services', JSON.stringify(filtered));
      return { message: 'Service deleted' };
    }
    const response = await this.client.delete(`/company/services/${id}`);
    return response.data;
  }

  async reorderServices(service_ids: string[]): Promise<CompanyService[]> {
    if (DEV_MODE) {
      await delay(300);
      const servicesStr = localStorage.getItem('dev_services');
      const services: CompanyService[] = servicesStr ? JSON.parse(servicesStr) : [];
      const reordered = service_ids.map((id, i) => {
        const service = services.find(s => s.id === id);
        if (service) return { ...service, display_order: i };
        return null;
      }).filter(Boolean) as CompanyService[];
      localStorage.setItem('dev_services', JSON.stringify(reordered));
      return reordered;
    }
    const response = await this.client.put('/company/services/reorder', {
      service_ids,
    });
    return response.data;
  }

  // ============================================
  // Generation endpoints
  // ============================================

  async generatePosts(data: GenerateRequest): Promise<GenerationResponse> {
    if (DEV_MODE) {
      // Honest latency: the real single-pass Gemini call is 10-18s PER PLATFORM,
      // not a flat 2.5s. A mock that returns faster than reality is how the
      // onboarding dead-wait shipped unnoticed. __zupaDev.speed('fast') opts out.
      await delay(DEV_GEN_MS_PER_PLATFORM * Math.max(1, data.platforms.length));
      await devFault('generatePosts');

      const posts = data.platforms.map((platform) => {
        const gen = buildDevGeneratedPlatform(platform);
        return {
          ...gen,
          tone_used:
            data.platform_tones?.find((t: any) => t.platform === platform)?.tone || 'professional',
        };
      });

      return {
        posts,
        generation_id: 'gen-' + Date.now(),
      };
    }
    const response = await this.client.post('/generate_with_tone', data);
    return response.data;
  }

  /**
   * Generate posts with SSE streaming for progressive display.
   * Each platform's posts are sent as soon as they're ready.
   */
  generatePostsStream(
    data: GenerateRequest,
    callbacks: {
      onStarted?: (info: { platforms: string[]; include_images: boolean }) => void;
      onAnalysis?: (analysis: import('@/types').ContentAnalysis) => void;
      onPrinciples?: (info: import('@/types').PrincipleSelectionEvent) => void;
      onPlatform?: (post: any) => void;
      onImages?: (images: { images: any[]; platform_recommendations: Record<string, number> }) => void;
      onComplete?: (info: { analysis: any; input_text: string; principles_selected?: string[]; shared_explanation?: string; timing?: { total_time_ms: number; analysis_time_ms: number; platform_times_ms: Record<string, number>; platforms_count: number; variations_count: number } }) => void;
      onDuplicate?: (data: { user_name: string; cached_data: { platforms: any[]; images?: any; complete?: any; ai_images?: Record<string, any> } }) => void;
      onError?: (error: string) => void;
    }
  ): { abort: () => void } {
    const abortController = new AbortController();

    if (DEV_MODE) {
      // THIS is the path the tool actually takes — Tool.tsx calls
      // generatePostsStream, not generatePosts. It used to run 500ms + 800ms per
      // platform on Acme Corp boilerplate with "(variation 2)" appended to pad
      // the count: faster, emptier and more successful than reality, on the most
      // important action in the product.
      //
      // Now: honest per-platform latency (so the AnalyzingPanel, the disabled
      // states and the "still working" copy are actually exercisable), and the
      // seeded workspace's own voice, so the output agrees with the brand the
      // rest of the app is showing.
      const startedAt = Date.now();
      (async () => {
        await delay(900);
        if (abortController.signal.aborted) return;
        callbacks.onStarted?.({ platforms: data.platforms, include_images: data.include_images || false });

        // Emit the SAME event sequence the real backend does (started -> analysis ->
        // principles -> platform -> complete). The staged status panel narrates from these,
        // so a mock that skipped `analysis` would leave a stage spinning in dev forever and
        // hide any bug in it.
        await delay(2500);
        if (abortController.signal.aborted) return;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        callbacks.onAnalysis?.({} as any);

        await delay(1800);
        if (abortController.signal.aborted) return;
        // The FULL selection — principles + reasoning + avoided + confidence.
        // PsychologyReasoningCard indexes reasoning[principle]; a partial payload
        // crashes it.
        callbacks.onPrinciples?.(DEV_PSYCH_SELECTION);

        for (const platform of data.platforms) {
          await delay(DEV_GEN_MS_PER_PLATFORM);
          // Abort has to actually stop the stream. Without this the mock kept
          // emitting platforms after the user cancelled.
          if (abortController.signal.aborted) return;
          const gen = buildDevGeneratedPlatform(platform);
          // Sprint 25 — fuel/lineage aren't in the shared dev fixture (out of this
          // piece's scope), so seed them here: enough to exercise the delta prompt
          // and the save-lineage path without a backend.
          callbacks.onPlatform?.({
            ...gen,
            variations: (gen.variations || []).map((v: any, i: number) => ({
              ...v,
              bank_item_ids: i === 0 ? ['dev-fact-1'] : [],
            })),
            fuel: { allowed: [], blocked: { credibility: 'no number in the bank yet' }, hint: 'Add one real number and Zupa can write proof posts.' },
          });
        }

        await delay(400);
        if (abortController.signal.aborted) return;
        // Send real timing. Without it `generationTiming` stays null and the post-generation
        // savings line NEVER renders in dev — so that whole surface was untestable locally.
        const elapsed = Date.now() - startedAt;
        callbacks.onComplete?.({
          analysis: {},
          input_text: data.text,
          timing: {
            total_time_ms: elapsed,
            analysis_time_ms: Math.round(elapsed * 0.2),
            platform_times_ms: Object.fromEntries(
              data.platforms.map((p) => [p, Math.round(elapsed / data.platforms.length)]),
            ),
            platforms_count: data.platforms.length,
            variations_count: data.platforms.length * 3,
          },
        });
      })();

      return { abort: () => abortController.abort() };
    }

    // Real SSE implementation
    const token = localStorage.getItem('access_token');
    let completeCalled = false;

    // Helper to process SSE events from text
    const processSSEText = (text: string) => {
      // Split by double newline (SSE event separator)
      const events = text.split('\n\n');
      
      for (const eventBlock of events) {
        if (!eventBlock.trim()) continue;
        
        const lines = eventBlock.split('\n');
        let eventType = '';
        let eventData = '';
        
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.slice(7).trim();
          } else if (line.startsWith('data: ')) {
            eventData = line.slice(6);
          }
        }
        
        if (eventType && eventData) {
          try {
            const parsedData = JSON.parse(eventData);
            
            switch (eventType) {
              case 'started':
                console.log('[SSE] Started:', parsedData);
                callbacks.onStarted?.(parsedData);
                break;
              case 'analysis':
                console.log('[SSE] Analysis received');
                callbacks.onAnalysis?.(parsedData);
                break;
              case 'principles':
                console.log('[SSE] Principles:', parsedData.principles);
                callbacks.onPrinciples?.(parsedData);
                break;
              case 'platform':
                console.log('[SSE] Platform:', parsedData.platform);
                callbacks.onPlatform?.(parsedData);
                break;
              case 'images':
                console.log('[SSE] Images received');
                callbacks.onImages?.(parsedData);
                break;
              case 'duplicate':
                console.log('[SSE] Duplicate detected');
                callbacks.onDuplicate?.(parsedData);
                break;
              case 'complete':
                console.log('[SSE] Complete');
                completeCalled = true;
                callbacks.onComplete?.(parsedData);
                break;
              case 'error':
                console.error('[SSE] Error:', parsedData.message);
                callbacks.onError?.(parsedData.message);
                break;
            }
          } catch (e) {
            console.error('[SSE] Failed to parse event data:', eventData, e);
          }
        }
      }
    };

    fetch(`${API_BASE_URL}/generate_stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(data),
      signal: abortController.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          // Handle Pydantic validation errors (detail is an array of objects)
          let errorMessage = `HTTP ${response.status}`;
          if (errorData.detail) {
            if (Array.isArray(errorData.detail)) {
              // Pydantic validation error format: [{type, loc, msg, input, ctx}, ...]
              const messages = errorData.detail.map((err: any) => {
                const location = err.loc?.join(' → ') || 'unknown field';
                return `${location}: ${err.msg || 'validation error'}`;
              });
              errorMessage = messages.join('; ');
            } else if (typeof errorData.detail === 'string') {
              errorMessage = errorData.detail;
            } else if (typeof errorData.detail === 'object' && errorData.detail.msg) {
              errorMessage = errorData.detail.msg;
            }
          }
          callbacks.onError?.(errorMessage);
          return;
        }

        const reader = response.body?.getReader();
        if (!reader) {
          callbacks.onError?.('No response body');
          return;
        }

        const decoder = new TextDecoder();
        let buffer = '';

        try {
          while (true) {
            const { done, value } = await reader.read();
            
            if (value) {
              buffer += decoder.decode(value, { stream: true });
              
              // Process complete events (separated by double newline)
              // Keep incomplete event in buffer
              const lastDoubleNewline = buffer.lastIndexOf('\n\n');
              if (lastDoubleNewline !== -1) {
                const completeEvents = buffer.substring(0, lastDoubleNewline + 2);
                buffer = buffer.substring(lastDoubleNewline + 2);
                processSSEText(completeEvents);
              }
            }
            
            if (done) {
              // Process any remaining buffer
              if (buffer.trim()) {
                processSSEText(buffer);
              }
              break;
            }
          }
        } finally {
          // Always ensure onComplete is called when stream ends
          if (!completeCalled) {
            console.log('[SSE] Stream ended without complete event, calling onComplete');
            callbacks.onComplete?.({ analysis: {}, input_text: data.text });
          }
        }
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error('[SSE] Stream error:', error);
          callbacks.onError?.(error.message || 'Stream failed');
        }
      });

    return { abort: () => abortController.abort() };
  }

  async getTones(): Promise<Record<string, TonePreset>> {
    if (DEV_MODE) {
      await delay(200);
      return {
        professional: { name: 'Professional', description: 'Formal and business-appropriate', characteristics: [], avoid: [], example_phrases: [], suitable_platforms: [] },
        conversational: { name: 'Conversational', description: 'Friendly and approachable', characteristics: [], avoid: [], example_phrases: [], suitable_platforms: [] },
        enthusiastic: { name: 'Enthusiastic', description: 'Energetic and exciting', characteristics: [], avoid: [], example_phrases: [], suitable_platforms: [] },
        educational: { name: 'Educational', description: 'Informative and helpful', characteristics: [], avoid: [], example_phrases: [], suitable_platforms: [] },
        inspirational: { name: 'Inspirational', description: 'Motivating and uplifting', characteristics: [], avoid: [], example_phrases: [], suitable_platforms: [] },
      };
    }
    const response = await this.client.get('/tones');
    return response.data.tones;
  }

  async getPlatformTones(platform: string): Promise<Record<string, TonePreset>> {
    if (DEV_MODE) {
      return this.getTones();
    }
    const response = await this.client.get(`/tones/platform/${platform}`);
    return response.data.tones;
  }

  async getPrinciples(): Promise<Record<string, Principle>> {
    if (DEV_MODE) {
      await delay(200);
      return {
        social_proof: { name: 'Social Proof', category: 'Influence', description: 'People follow the actions of others', source: 'Cialdini' },
        scarcity: { name: 'Scarcity', category: 'Urgency', description: 'Limited availability increases perceived value', source: 'Cialdini' },
      };
    }
    const response = await this.client.get('/principles');
    return response.data.principles;
  }

  async getPrincipleDetails(id: string): Promise<Principle> {
    if (DEV_MODE) {
      await delay(200);
      return { name: id, category: 'General', description: 'Mock principle', source: 'Mock' };
    }
    const response = await this.client.get(`/principles/${id}`);
    return response.data.principle;
  }

  // ============================================
  // Post selection (Learning system)
  // ============================================

  async selectPost(data: PostSelectRequest): Promise<PostSelectResponse> {
    if (DEV_MODE) {
      await delay(300);
      return {
        message: 'Post selection recorded',
        quality_score: 5,
        added_to_examples: true,
      };
    }
    const response = await this.client.post('/company/posts/select', data);
    return response.data;
  }

  // ============================================
  // Collections endpoints
  // ============================================

  async getCollections(): Promise<Collection[]> {
    if (DEV_MODE) {
      await delay(200);
      if (await devFault('getCollections')) return [];
      // post_count is computed from the live posts rather than stored, so a
      // collection card can never claim a count that disagrees with the posts
      // you get when you open it.
      return collectionsWithCounts();
    }
    const response = await this.client.get('/collections');
    return response.data;
  }

  async createCollection(name: string, description?: string): Promise<Collection> {
    if (DEV_MODE) {
      await delay(300);
      const stored = localStorage.getItem('dev_collections');
      const collections: Collection[] = stored ? JSON.parse(stored) : [];
      
      const newCollection: Collection = {
        id: 'collection-' + Date.now(),
        name,
        description,
        post_count: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      
      collections.push(newCollection);
      localStorage.setItem('dev_collections', JSON.stringify(collections));
      return newCollection;
    }
    const response = await this.client.post('/collections', {
      name,
      description,
    });
    return response.data;
  }

  async getCollection(id: string): Promise<Collection> {
    if (DEV_MODE) {
      await delay(200);
      const collection = collectionsWithCounts().find(c => c.id === id);
      if (!collection) throw new Error('Collection not found');
      return collection;
    }
    const response = await this.client.get(`/collections/${id}`);
    return response.data;
  }

  async updateCollection(id: string, name?: string, description?: string): Promise<Collection> {
    if (DEV_MODE) {
      await delay(300);
      const stored = localStorage.getItem('dev_collections');
      const collections: Collection[] = stored ? JSON.parse(stored) : [];
      const index = collections.findIndex(c => c.id === id);
      if (index === -1) throw new Error('Collection not found');
      
      if (name) collections[index].name = name;
      if (description !== undefined) collections[index].description = description;
      collections[index].updated_at = new Date().toISOString();
      
      localStorage.setItem('dev_collections', JSON.stringify(collections));
      return collections[index];
    }
    const response = await this.client.patch(`/collections/${id}`, {
      name,
      description,
    });
    return response.data;
  }

  async deleteCollection(id: string): Promise<MessageResponse> {
    if (DEV_MODE) {
      await delay(300);
      const stored = localStorage.getItem('dev_collections');
      const collections: Collection[] = stored ? JSON.parse(stored) : [];
      localStorage.setItem('dev_collections', JSON.stringify(collections.filter(c => c.id !== id)));
      // Deleting a collection must not delete its posts — they go back to
      // uncollected. Leaving them pointing at a dead collection_id would strand
      // them: filtered out of every collection view AND out of "all posts".
      saveDevPosts(
        devPosts().map((p) => (p.collection_id === id ? { ...p, collection_id: null } : p)),
      );
      return { message: 'Collection deleted' };
    }
    const response = await this.client.delete(`/collections/${id}`);
    return response.data;
  }

  // ============================================
  // Posts endpoints
  // ============================================

  async createPost(data: {
    platform: string;
    content: string;
    original_text: string;
    principles_used?: string[];
    explanation?: string;
    hashtags?: string[];
    posting_tips?: string;
    tone_used?: string;
    is_favorited?: boolean;
    image_base64?: string | null;
    image_mime_type?: string | null;
    is_carousel?: boolean;
    carousel_slide_count?: number;
    // Slide entries support both image carousels (all fields) and Sprint 14
    // text-only carousels (slide_number + text_overlay_data only; image fields absent).
    carousel_slides?: Array<{
      slide_number: number;
      image_base64?: string | null;
      image_mime_type?: string;
      image_prompt?: string;
      slide_theme?: string;
      text_overlay_data?: unknown;
      edit_count?: number;
    }>;
    // Quality metrics from generation pipeline (optional)
    deterministic_score?: number | null;
    grade?: string | null;
    bold_count?: number | null;
    hook_type?: string | null;
    humanizer_used?: boolean | null;
    regen_count?: number | null;
    // Sprint 25 — the bank items this specific variation was written from, so the
    // post keeps its lineage back to the fact/moment/verbatim bank after saving.
    bank_item_ids?: string[] | null;
  }): Promise<SavedPost> {
    if (DEV_MODE) {
      await delay(300);
      await devFault('createPost');
      const post: SavedPost = {
        id: 'post-' + Date.now(),
        platform: data.platform as any,
        content: data.content,
        original_text: data.original_text,
        principles_used: data.principles_used || [],
        explanation: data.explanation || null,
        hashtags: data.hashtags || [],
        posting_tips: data.posting_tips || null,
        tone_used: data.tone_used || null,
        is_favorited: data.is_favorited || false,
        collection_id: null,
        image_base64: data.image_base64 || null,
        image_mime_type: data.image_mime_type || null,
        text_overlay_data: null,
        created_at: new Date().toISOString(),
      };
      // Persist it. The mock used to return the post and drop it on the floor,
      // so saving from the tool never showed up in My Posts — which is exactly
      // the moment the tour hands off to the output pages.
      saveDevPosts([post, ...devPosts()]);
      return post;
    }
    const response = await this.client.post('/posts', data);
    return response.data;
  }

  async makeIllustrative(data: {
    post_text: string;
    platform: string;
    workspace_id: string;
    principle_id?: string;
  }): Promise<{ post_text: string; precheck: any }> {
    const response = await this.client.post('/make_illustrative', data);
    return response.data;
  }

  /**
   * DEV_MODE: a real little store over `dev_posts`, not a hardcoded empty page.
   * It used to `return { posts: [], total: 0 }` unconditionally — so My Posts,
   * every collection, favourites and search were all permanently blank, and the
   * dashboard's fabricated "42 posts" had nothing to contradict it. Filters and
   * pagination are applied here for the same reason: a mock that ignores its
   * params makes a broken filter look like it works.
   */
  async getPosts(params?: {
    page?: number;
    per_page?: number;
    platform?: string;
    is_favorited?: boolean;
    collection_id?: string;
    search?: string;
  }): Promise<PaginatedPosts> {
    if (DEV_MODE) {
      await delay(200);
      const page = params?.page ?? 1;
      const per_page = params?.per_page ?? 20;
      if (await devFault('getPosts')) {
        return { posts: [], total: 0, page, per_page, total_pages: 0 };
      }

      const needle = params?.search?.trim().toLowerCase();
      const matches = devPosts()
        .filter((p) => (params?.platform ? p.platform === params.platform : true))
        .filter((p) => (params?.is_favorited ? p.is_favorited : true))
        .filter((p) => (params?.collection_id ? p.collection_id === params.collection_id : true))
        .filter((p) =>
          needle
            ? p.content.toLowerCase().includes(needle) ||
              p.original_text.toLowerCase().includes(needle)
            : true,
        )
        .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at));

      const start = (page - 1) * per_page;
      return {
        posts: matches.slice(start, start + per_page),
        total: matches.length,
        page,
        per_page,
        total_pages: Math.max(1, Math.ceil(matches.length / per_page)),
      };
    }
    const response = await this.client.get('/posts', { params });
    return response.data;
  }

  async getFavorites(page = 1, per_page = 20): Promise<PaginatedPosts> {
    if (DEV_MODE) {
      if (await devFault('getFavorites')) {
        return { posts: [], total: 0, page, per_page, total_pages: 0 };
      }
      return this.getPosts({ page, per_page, is_favorited: true });
    }
    const response = await this.client.get('/posts/favorites', {
      params: { page, per_page },
    });
    return response.data;
  }

  async getPost(id: string): Promise<SavedPost> {
    if (DEV_MODE) {
      await delay(150);
      await devFault('getPost');
      const post = devPosts().find((p) => p.id === id);
      if (!post) throw new Error('Post not found');
      return post;
    }
    const response = await this.client.get(`/posts/${id}`);
    return response.data;
  }

  /**
   * DEV_MODE: these four all used to `throw new Error('Not implemented in dev
   * mode')`. With the posts list hardcoded empty there was nothing to act on, so
   * the throw was invisible — favouriting, moving, editing and re-imaging a post
   * were untestable without a backend. They mutate `dev_posts` now, so the whole
   * lifecycle (create → read → modify → delete) actually runs in dev.
   */
  private async devMutatePost(id: string, patch: Partial<SavedPost>): Promise<SavedPost> {
    await delay(200);
    const posts = devPosts();
    const idx = posts.findIndex((p) => p.id === id);
    if (idx === -1) throw new Error('Post not found');
    const updated = { ...posts[idx], ...patch };
    posts[idx] = updated;
    saveDevPosts(posts);
    return updated;
  }

  async toggleFavorite(id: string, is_favorited: boolean): Promise<SavedPost> {
    if (DEV_MODE) {
      return this.devMutatePost(id, { is_favorited });
    }
    const response = await this.client.patch(`/posts/${id}/favorite`, {
      is_favorited,
    });
    return response.data;
  }

  async moveToCollection(id: string, collection_id: string | null): Promise<SavedPost> {
    if (DEV_MODE) {
      return this.devMutatePost(id, { collection_id });
    }
    const response = await this.client.patch(`/posts/${id}/collection`, {
      collection_id,
    });
    return response.data;
  }

  async updatePostContent(id: string, content: string): Promise<SavedPost> {
    if (DEV_MODE) {
      return this.devMutatePost(id, { content });
    }
    const response = await this.client.patch(`/posts/${id}/content`, { content });
    return response.data;
  }

  async updatePostImage(
    id: string,
    image_base64: string,
    image_mime_type: string = 'image/png',
    text_overlay_data?: Record<string, any> | null,
  ): Promise<SavedPost> {
    if (DEV_MODE) {
      return this.devMutatePost(id, {
        image_base64,
        image_mime_type,
        text_overlay_data: (text_overlay_data ?? null) as SavedPost['text_overlay_data'],
      });
    }
    const response = await this.client.patch(`/posts/${id}/image`, {
      image_base64,
      image_mime_type,
      text_overlay_data: text_overlay_data ?? null,
    });
    return response.data;
  }

  async deletePost(id: string): Promise<MessageResponse> {
    if (DEV_MODE) {
      await delay(300);
      await devFault('deletePost');
      // Actually remove it — a "deleted" post that reappears on the next fetch
      // is worse than no mock at all.
      saveDevPosts(devPosts().filter((p) => p.id !== id));
      return { message: 'Post deleted' };
    }
    const response = await this.client.delete(`/posts/${id}`);
    return response.data;
  }

  // ============================================
  // Health check
  // ============================================

  // ============================================
  // Subscription endpoints
  // ============================================

  async getSubscriptionInfo(): Promise<SubscriptionInfo> {
    if (DEV_MODE) {
      await delay(200);
      return {
        tier: 'pro',
        is_pro: true,
        is_basic_plus: false,
        is_trial: true,
        trial_days_remaining: 15,
        trial_ends_at: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString(),
        subscription_ends_at: null,
        trial_ended: false,
        generation_limit: 50,
        generations_used: 0,
        generations_remaining: 50,
        platforms_per_generation: 4,
        variations_per_platform: 3,
        image_limit: 5,
        workspace_limit: 8,
        prompt_limit: 70,
        ai_images_limit: 30,
        ai_images_used: 0,
        ai_images_remaining: 30,
        ai_image_edits_per_image: 3,
        carousels_per_month: 10,
        carousels_used: 0,
        carousels_remaining: 10,
        can_use_carousels: true,
        can_use_company_intelligence: true,
        can_use_learning_system: true,
        can_use_ai_images: true,
        can_use_context_aware_images: true,
        can_use_service_specific_posts: true,
        can_post_linkedin: true,
        can_post_instagram: true,
        can_post_facebook: true,
        can_post_twitter: true,
      };
    }
    const response = await this.client.get('/auth/subscription');
    return response.data;
  }

  // ============================================
  // Health check
  // ============================================

  async healthCheck(): Promise<{ status: string; message: string }> {
    if (DEV_MODE) {
      return { status: 'ok', message: 'Dev mode active' };
    }
    const response = await this.client.get('/health');
    return response.data;
  }

  // ============================================
  // User API Keys - REMOVED
  // ============================================
  // Pexels and Unsplash API keys are now stored in .env file on the server.
  // The getApiKeys() and updateApiKeys() methods have been removed.
  // Twitter/social API keys are still managed via getSocialKeys() and updateSocialKeys().

  // ============================================
  // Social Media Connections & Posting
  // ============================================

  async getSocialPlatforms(): Promise<SocialPlatform[]> {
    if (DEV_MODE) {
      await delay(200);
      // Instagram and Facebook are configured here so the accounts seeded for
      // them actually surface — an unconfigured platform hides its connected
      // account entirely, which would have made the seeded Facebook account
      // (and its expired-token / reconnect state) unreachable in dev.
      // Twitter stays unconfigured on purpose: it is a genuinely different flow
      // (the user brings their own API keys), and that branch needs to render too.
      return [
        { platform: 'linkedin', is_configured: true, display_name: 'LinkedIn', description: 'Post to your LinkedIn profile or company pages' },
        { platform: 'twitter', is_configured: false, display_name: 'X (Twitter)', description: 'Post to your X (Twitter) account using your own free developer keys' },
        { platform: 'facebook', is_configured: true, display_name: 'Facebook', description: 'Post to Facebook Pages you manage' },
        { platform: 'instagram', is_configured: true, display_name: 'Instagram', description: 'Post to Instagram Business/Creator accounts' },
      ];
    }
    const response = await this.client.get('/social/platforms');
    return response.data;
  }

  async getSocialAccounts(): Promise<ConnectedSocialAccount[]> {
    if (DEV_MODE) {
      await delay(200);
      if (await devFault('getSocialAccounts')) return [];
      // Seeded: two healthy accounts and one with an expired token, so both the
      // connected state and the reconnect/error state are reachable. Nothing was
      // ever connected here before, which made "post it from Zupa" — the payoff
      // the tour builds to — untestable end to end.
      return devRead<ConnectedSocialAccount[]>(DEV_KEYS.social, []);
    }
    const response = await this.client.get('/social/accounts');
    return response.data;
  }

  async getSocialConnectUrl(platform: string): Promise<{ authorization_url: string }> {
    if (DEV_MODE) {
      throw new Error('Social connections not available in dev mode');
    }
    const response = await this.client.get(`/social/connect/${platform}/url`);
    return response.data;
  }

  async handleSocialCallback(platform: string, code: string, state: string): Promise<ConnectedSocialAccount> {
    if (DEV_MODE) {
      throw new Error('Social connections not available in dev mode');
    }
    const response = await this.client.post(`/social/connect/${platform}/callback`, { code, state });
    return response.data;
  }

  async disconnectSocialAccount(accountId: string): Promise<void> {
    if (DEV_MODE) {
      await delay(200);
      // Now that accounts exist in dev, disconnect has to actually work — an
      // account that "disconnects" and comes back on refetch is a new lie.
      const accounts = devRead<ConnectedSocialAccount[]>(DEV_KEYS.social, []);
      devWrite(DEV_KEYS.social, accounts.filter((a) => a.id !== accountId));
      return;
    }
    await this.client.delete(`/social/accounts/${accountId}`);
  }

  async createSocialPost(accountId: string, content: string, imageUrl?: string, carouselSlideImages?: string[], isCarousel?: boolean): Promise<SocialPostResult> {
    if (DEV_MODE) {
      throw new Error('Social posting not available in dev mode');
    }
    const response = await this.client.post('/social/post', {
      account_id: accountId,
      content,
      image_url: imageUrl,
      carousel_slide_images: isCarousel ? carouselSlideImages : undefined,
      is_carousel: isCarousel || false,
    });
    return response.data;
  }

  async getUserSocialKeys(): Promise<UserSocialKeys> {
    if (DEV_MODE) {
      await delay(200);
      const stored = localStorage.getItem('dev_social_keys');
      if (stored) {
        const keys = JSON.parse(stored);
        return {
          twitter_api_key: keys.twitter_api_key ? '****' + keys.twitter_api_key.slice(-4) : null,
          twitter_api_secret: keys.twitter_api_secret ? '****' : null,
          has_twitter_keys: !!(keys.twitter_api_key && keys.twitter_api_secret),
        };
      }
      return {
        twitter_api_key: null,
        twitter_api_secret: null,
        has_twitter_keys: false,
      };
    }
    const response = await this.client.get('/auth/social-keys');
    return response.data;
  }

  async updateUserSocialKeys(data: UserSocialKeysUpdate): Promise<UserSocialKeys> {
    if (DEV_MODE) {
      await delay(300);
      const current = JSON.parse(localStorage.getItem('dev_social_keys') || '{}');
      const updated = { ...current };
      
      Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined) {
          if (value === '') {
            delete updated[key];
          } else {
            updated[key] = value;
          }
        }
      });
      
      localStorage.setItem('dev_social_keys', JSON.stringify(updated));
      
      return {
        twitter_api_key: updated.twitter_api_key ? '****' + updated.twitter_api_key.slice(-4) : null,
        twitter_api_secret: updated.twitter_api_secret ? '****' : null,
        has_twitter_keys: !!(updated.twitter_api_key && updated.twitter_api_secret),
      };
    }
    const response = await this.client.patch('/auth/social-keys', data);
    return response.data;
  }
  // ============================================
  // Workspace endpoints
  // ============================================

  async getWorkspaceLimits(): Promise<import('@/types').WorkspaceLimits> {
    if (DEV_MODE) {
      await delay(200);
      const workspaces = JSON.parse(localStorage.getItem('dev_workspaces') || '[]');
      return {
        current_count: workspaces.length,
        max_allowed: 8,
        can_create: workspaces.length < 8,
        tier: 'pro',
      };
    }
    const response = await this.client.get('/workspaces/limits');
    return response.data;
  }

  async getWorkspaces(): Promise<import('@/types').Workspace[]> {
    if (DEV_MODE) {
      await delay(200);
      const stored = localStorage.getItem('dev_workspaces');
      if (stored) {
        return JSON.parse(stored);
      }
      // Create default workspace if none exist
      const defaultWorkspace: import('@/types').Workspace = {
        id: 'ws-default',
        name: 'My Workspace',
        logo_url: null,
        company_url: null,
        company_name: null,
        industry: null,
        description: null,
        target_audience: null,
        brand_voice: null,
        default_tone: null,
        // Visual Brand DNA
        brand_colors: null,
        primary_color: null,
        visual_style: null,
        photography_style: null,
        visual_mood: null,
        brand_fonts: null,
        logo_description: null,
        extracted_logo_url: null,
        visual_brand_analyzed: false,
        // Company Voice
        brand_voice_settings: null,
        workspace_type: 'company',
        logistics_type: null,
        region: null,
        autopilot_enabled: true,
        // Metadata
        is_default: true,
        is_onboarded: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        deleted_at: null,
      };
      localStorage.setItem('dev_workspaces', JSON.stringify([defaultWorkspace]));
      localStorage.setItem('dev_active_workspace_id', defaultWorkspace.id);
      return [defaultWorkspace];
    }
    // Add cache-busting timestamp to ensure fresh data
    const response = await this.client.get('/workspaces', {
      params: { _t: Date.now() },
      headers: { 'Cache-Control': 'no-cache' }
    });
    return (response.data.workspaces || []).map((w: any) => ({
      ...w,
      logo_url: fixUploadUrl(w.logo_url),
      extracted_logo_url: fixUploadUrl(w.extracted_logo_url),
    }));
  }

  async getActiveWorkspace(): Promise<import('@/types').WorkspaceFull | null> {
    if (DEV_MODE) {
      await delay(200);
      const workspaces = JSON.parse(localStorage.getItem('dev_workspaces') || '[]');
      const activeId = localStorage.getItem('dev_active_workspace_id');
      const active = workspaces.find((w: any) => w.id === activeId) || workspaces[0];
      if (!active) return null;
      return {
        ...active,
        services: JSON.parse(localStorage.getItem(`dev_ws_services_${active.id}`) || '[]'),
        social_connections: [],
        learning_stats: {},
      };
    }
    const response = await this.client.get('/workspaces/active');
    // Backend returns nested structure: { workspace: {...}, services: [...], ... }
    // Frontend expects flat structure: { id, name, ..., services: [...] }
    if (!response.data) return null;
    const { workspace, services, social_connections, learning_stats } = response.data;
    if (!workspace) return null;
    return {
      ...workspace,
      logo_url: fixUploadUrl(workspace.logo_url),
      extracted_logo_url: fixUploadUrl(workspace.extracted_logo_url),
      services: services || [],
      social_connections: social_connections || [],
      learning_stats: learning_stats || {},
    };
  }

  async getWorkspace(id: string): Promise<import('@/types').WorkspaceFull> {
    if (DEV_MODE) {
      await delay(200);
      const workspaces = JSON.parse(localStorage.getItem('dev_workspaces') || '[]');
      const workspace = workspaces.find((w: any) => w.id === id);
      if (!workspace) throw new Error('Workspace not found');
      return {
        ...workspace,
        services: JSON.parse(localStorage.getItem(`dev_ws_services_${id}`) || '[]'),
        social_connections: [],
        learning_stats: {},
      };
    }
    const response = await this.client.get(`/workspaces/${id}`);
    // Backend returns nested structure: { workspace: {...}, services: [...], ... }
    // Frontend expects flat structure: { id, name, ..., services: [...] }
    const { workspace, services, social_connections, learning_stats } = response.data;
    return {
      ...workspace,
      logo_url: fixUploadUrl(workspace.logo_url),
      extracted_logo_url: fixUploadUrl(workspace.extracted_logo_url),
      services: services || [],
      social_connections: social_connections || [],
      learning_stats: learning_stats || {},
    };
  }

  async createWorkspace(data: import('@/types').WorkspaceCreateRequest): Promise<import('@/types').Workspace> {
    if (DEV_MODE) {
      await delay(300);
      const workspaces = JSON.parse(localStorage.getItem('dev_workspaces') || '[]');
      const newWorkspace: import('@/types').Workspace = {
        id: 'ws-' + Date.now(),
        name: data.name,
        logo_url: data.logo_url || null,
        company_url: data.company_url || null,
        company_name: null,
        industry: data.industry || null,
        description: data.description || null,
        target_audience: data.target_audience || null,
        brand_voice: data.brand_voice || null,
        default_tone: null,
        // Visual Brand DNA
        brand_colors: null,
        primary_color: null,
        visual_style: null,
        photography_style: null,
        visual_mood: null,
        brand_fonts: null,
        logo_description: null,
        extracted_logo_url: null,
        visual_brand_analyzed: false,
        // Company Voice
        brand_voice_settings: null,
        workspace_type: data.workspace_type || 'company',
        logistics_type: data.logistics_type || null,
        region: data.region || null,
        autopilot_enabled: true,
        // Metadata
        is_default: false,
        is_onboarded: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        deleted_at: null,
      };
      workspaces.push(newWorkspace);
      localStorage.setItem('dev_workspaces', JSON.stringify(workspaces));
      return newWorkspace;
    }
    const response = await this.client.post('/workspaces', data);
    return response.data;
  }

  async updateWorkspace(id: string, data: import('@/types').WorkspaceUpdateRequest): Promise<import('@/types').Workspace> {
    if (DEV_MODE) {
      await delay(300);
      const workspaces = JSON.parse(localStorage.getItem('dev_workspaces') || '[]');
      const index = workspaces.findIndex((w: any) => w.id === id);
      if (index === -1) throw new Error('Workspace not found');
      workspaces[index] = { ...workspaces[index], ...data, updated_at: new Date().toISOString() };
      localStorage.setItem('dev_workspaces', JSON.stringify(workspaces));
      return workspaces[index];
    }
    const response = await this.client.put(`/workspaces/${id}`, data);
    return response.data;
  }

  async deleteWorkspace(id: string): Promise<MessageResponse> {
    if (DEV_MODE) {
      await delay(300);
      const workspaces = JSON.parse(localStorage.getItem('dev_workspaces') || '[]');
      const index = workspaces.findIndex((w: any) => w.id === id);
      if (index === -1) throw new Error('Workspace not found');
      workspaces[index].deleted_at = new Date().toISOString();
      localStorage.setItem('dev_workspaces', JSON.stringify(workspaces));
      return { message: 'Workspace deleted' };
    }
    const response = await this.client.delete(`/workspaces/${id}`);
    return response.data;
  }

  async activateWorkspace(id: string): Promise<import('@/types').Workspace> {
    if (DEV_MODE) {
      await delay(200);
      localStorage.setItem('dev_active_workspace_id', id);
      const workspaces = JSON.parse(localStorage.getItem('dev_workspaces') || '[]');
      const workspace = workspaces.find((w: any) => w.id === id);
      if (!workspace) throw new Error('Workspace not found');
      return workspace;
    }
    const response = await this.client.post(`/workspaces/${id}/activate`);
    // Backend returns: { message: string, active_workspace: {...} }
    return response.data.active_workspace;
  }

  async getDeletedWorkspaces(): Promise<import('@/types').DeletedWorkspace[]> {
    if (DEV_MODE) {
      await delay(200);
      const workspaces = JSON.parse(localStorage.getItem('dev_workspaces') || '[]');
      return workspaces
        .filter((w: any) => w.deleted_at)
        .map((w: any) => ({
          ...w,
          days_until_permanent: 20,
          can_recover: true,
        }));
    }
    const response = await this.client.get('/workspaces/deleted');
    // Backend returns { id, name, deleted_at, days_until_permanent_deletion } per
    // workspace (no can_recover field) — /workspaces/deleted already filters to
    // only workspaces still inside the recovery window, so every row returned
    // is recoverable. Map to the frontend DeletedWorkspace contract.
    return (response.data.workspaces || []).map((w: any) => ({
      ...w,
      days_until_permanent: w.days_until_permanent_deletion,
      can_recover: true,
    }));
  }

  async restoreWorkspace(id: string): Promise<import('@/types').Workspace> {
    if (DEV_MODE) {
      await delay(300);
      const workspaces = JSON.parse(localStorage.getItem('dev_workspaces') || '[]');
      const index = workspaces.findIndex((w: any) => w.id === id);
      if (index === -1) throw new Error('Workspace not found');
      workspaces[index].deleted_at = null;
      localStorage.setItem('dev_workspaces', JSON.stringify(workspaces));
      return workspaces[index];
    }
    const response = await this.client.post(`/workspaces/${id}/restore`);
    return response.data;
  }

  // Logo upload for workspaces
  async uploadWorkspaceLogo(workspaceId: string, file: File): Promise<import('@/types').Workspace> {
    if (DEV_MODE) {
      await delay(500);
      // In dev mode, create a local object URL for the file
      const workspaces = JSON.parse(localStorage.getItem('dev_workspaces') || '[]');
      const index = workspaces.findIndex((w: any) => w.id === workspaceId);
      if (index === -1) throw new Error('Workspace not found');
      // Create a blob URL for preview (not persistent but works for dev)
      const blobUrl = URL.createObjectURL(file);
      workspaces[index].logo_url = blobUrl;
      workspaces[index].updated_at = new Date().toISOString();
      localStorage.setItem('dev_workspaces', JSON.stringify(workspaces));
      return workspaces[index];
    }
    
    // Create FormData for file upload
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await this.client.post(`/workspaces/${workspaceId}/logo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async deleteWorkspaceLogo(workspaceId: string): Promise<import('@/types').Workspace> {
    if (DEV_MODE) {
      await delay(300);
      const workspaces = JSON.parse(localStorage.getItem('dev_workspaces') || '[]');
      const index = workspaces.findIndex((w: any) => w.id === workspaceId);
      if (index === -1) throw new Error('Workspace not found');
      workspaces[index].logo_url = null;
      workspaces[index].updated_at = new Date().toISOString();
      localStorage.setItem('dev_workspaces', JSON.stringify(workspaces));
      return workspaces[index];
    }
    const response = await this.client.delete(`/workspaces/${workspaceId}/logo`);
    return response.data;
  }

  async researchWorkspaceCompany(workspaceId: string, websiteUrl: string, contentUrl?: string, manualDescription?: string): Promise<import('@/types').WorkspaceResearchResponse> {
    if (DEV_MODE) {
      await delay(2000);
      const domain = websiteUrl.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
      const companyName = domain.split('.')[0].charAt(0).toUpperCase() + domain.split('.')[0].slice(1);
      return {
        name: companyName,
        website_url: websiteUrl,
        industry: 'Technology',
        description: `${companyName} is a leading provider of innovative solutions.`,
        target_audience: 'Small to medium businesses',
        brand_voice: 'Professional and innovative',
        suggested_services: [
          { name: 'Consulting', description: 'Expert advice and strategy' },
          { name: 'Development', description: 'Custom software solutions' },
        ],
        // Visual Brand DNA
        brand_colors: ['#3B82F6', '#1E40AF', '#FFFFFF'],
        primary_color: '#3B82F6',
        visual_style: 'Modern minimal with blue accents',
        photography_style: 'Professional corporate',
        visual_mood: 'Trustworthy and innovative',
        brand_fonts: ['Inter', 'Roboto'],
        logo_description: 'Abstract symbol with company wordmark',
        extracted_logo_url: `https://${domain}/favicon.ico`,
        visual_brand_analyzed: true,
      };
    }
    const response = await this.client.post(`/workspaces/${workspaceId}/research`, {
      website_url: websiteUrl,
      ...(contentUrl ? { content_url: contentUrl } : {}),
      ...(manualDescription ? { manual_description: manualDescription } : {}),
    });
    return response.data;
  }

  // Workspace Services
  async getWorkspaceServices(workspaceId: string): Promise<import('@/types').WorkspaceService[]> {
    if (DEV_MODE) {
      await delay(200);
      return JSON.parse(localStorage.getItem(`dev_ws_services_${workspaceId}`) || '[]');
    }
    const response = await this.client.get(`/workspaces/${workspaceId}/services`);
    return response.data;
  }

  // ==========================================
  // Company Voice
  // ==========================================

  async getCompanyVoice(workspaceId: string): Promise<import('@/types/workspace').CompanyVoiceResponse> {
    if (DEV_MODE) {
      await delay(200);
      const stored = localStorage.getItem(`dev_ws_voice_${workspaceId}`);
      const settings = stored ? JSON.parse(stored) : {};
      return {
        ...settings,
        has_voice_configured: (settings.writing_samples?.length || 0) >= 3,
      };
    }
    const response = await this.client.get(`/workspaces/${workspaceId}/voice`);
    return response.data;
  }

  async updateCompanyVoice(
    workspaceId: string,
    data: import('@/types/workspace').CompanyVoiceSettings
  ): Promise<import('@/types/workspace').CompanyVoiceResponse> {
    if (DEV_MODE) {
      await delay(300);
      const stored = localStorage.getItem(`dev_ws_voice_${workspaceId}`);
      const current = stored ? JSON.parse(stored) : {};
      const updated = { ...current, ...data };
      localStorage.setItem(`dev_ws_voice_${workspaceId}`, JSON.stringify(updated));
      return {
        ...updated,
        has_voice_configured: (updated.writing_samples?.length || 0) >= 3,
      };
    }
    const response = await this.client.put(`/workspaces/${workspaceId}/voice`, data);
    return response.data;
  }

  async analyzeCompanyVoice(workspaceId: string): Promise<import('@/types/workspace').CompanyVoiceResponse> {
    if (DEV_MODE) {
      await delay(3000);
      const stored = localStorage.getItem(`dev_ws_voice_${workspaceId}`);
      const settings = stored ? JSON.parse(stored) : {};
      return {
        ...settings,
        has_voice_configured: (settings.writing_samples?.length || 0) >= 3,
        voice_analysis: {
          sentence_style: { avg_length: 'short', uses_fragments: true, uses_questions: true, paragraph_length: 'short' },
          vocabulary_level: 'simple',
          perspective: 'first_person',
          formatting_preferences: { uses_emojis: false, uses_bullets: false, uses_bold: true, uses_hashtags: false },
          content_patterns: { leads_with_data: true, uses_stories: true, uses_examples: true, asks_audience: true },
          signature_phrases: ['Here is what I found...', 'The data shows...'],
          words_they_use: ['built', 'shipped', 'scored'],
          words_they_avoid: ['leverage', 'synergy'],
          overall_vibe: 'Punchy, data-driven founder.',
          analyzed_at: new Date().toISOString(),
          samples_hash: 'mock',
        },
      };
    }
    const response = await this.client.post(`/workspaces/${workspaceId}/voice/analyze`);
    return response.data;
  }

  // ==========================================
  // Workspace Services
  // ==========================================

  async createWorkspaceService(workspaceId: string, data: import('@/types').ServiceCreateRequest): Promise<import('@/types').WorkspaceService> {
    if (DEV_MODE) {
      await delay(300);
      const services = JSON.parse(localStorage.getItem(`dev_ws_services_${workspaceId}`) || '[]');
      const newService: import('@/types').WorkspaceService = {
        id: 'svc-' + Date.now(),
        workspace_id: workspaceId,
        name: data.name,
        description: data.description || null,
        display_order: services.length,
        is_active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      services.push(newService);
      localStorage.setItem(`dev_ws_services_${workspaceId}`, JSON.stringify(services));
      return newService;
    }
    const response = await this.client.post(`/workspaces/${workspaceId}/services`, data);
    return response.data;
  }

  async updateWorkspaceService(
    workspaceId: string,
    serviceId: string,
    data: import('@/types').ServiceUpdateRequest
  ): Promise<import('@/types').WorkspaceService> {
    if (DEV_MODE) {
      await delay(300);
      const services = JSON.parse(localStorage.getItem(`dev_ws_services_${workspaceId}`) || '[]');
      const index = services.findIndex((s: any) => s.id === serviceId);
      if (index === -1) throw new Error('Service not found');
      services[index] = { ...services[index], ...data, updated_at: new Date().toISOString() };
      localStorage.setItem(`dev_ws_services_${workspaceId}`, JSON.stringify(services));
      return services[index];
    }
    const response = await this.client.put(`/workspaces/${workspaceId}/services/${serviceId}`, data);
    return response.data;
  }

  async deleteWorkspaceService(workspaceId: string, serviceId: string): Promise<MessageResponse> {
    if (DEV_MODE) {
      await delay(300);
      const services = JSON.parse(localStorage.getItem(`dev_ws_services_${workspaceId}`) || '[]');
      const filtered = services.filter((s: any) => s.id !== serviceId);
      localStorage.setItem(`dev_ws_services_${workspaceId}`, JSON.stringify(filtered));
      return { message: 'Service deleted' };
    }
    const response = await this.client.delete(`/workspaces/${workspaceId}/services/${serviceId}`);
    return response.data;
  }

  async reorderWorkspaceServices(workspaceId: string, serviceIds: string[]): Promise<import('@/types').WorkspaceService[]> {
    if (DEV_MODE) {
      await delay(300);
      const services = JSON.parse(localStorage.getItem(`dev_ws_services_${workspaceId}`) || '[]');
      const reordered = serviceIds.map((id, i) => {
        const service = services.find((s: any) => s.id === id);
        if (service) return { ...service, display_order: i };
        return null;
      }).filter(Boolean);
      localStorage.setItem(`dev_ws_services_${workspaceId}`, JSON.stringify(reordered));
      return reordered;
    }
    const response = await this.client.put(`/workspaces/${workspaceId}/services/reorder`, { service_ids: serviceIds });
    return response.data;
  }

  // ============================================
  // Persona endpoints
  // ============================================

  async getPersonas(workspaceId: string): Promise<import('@/types').PersonaListResponse> {
    if (DEV_MODE) {
      await delay(200);
      const personas = JSON.parse(localStorage.getItem(`dev_ws_personas_${workspaceId}`) || '[]');
      return { personas, count: personas.length };
    }
    const response = await this.client.get(`/workspaces/${workspaceId}/personas`);
    return response.data;
  }

  async createPersona(workspaceId: string, data: import('@/types').PersonaCreateRequest): Promise<import('@/types').WorkspacePersona> {
    const response = await this.client.post(`/workspaces/${workspaceId}/personas`, data);
    return response.data;
  }

  async generatePersonas(workspaceId: string, data: import('@/types').PersonaGenerateRequest): Promise<import('@/types').PersonaListResponse> {
    if (DEV_MODE) {
      // Persona generation is a real model call — several seconds, not 800ms.
      await delay(5000);
      await devFault('generatePersonas');
      // Return (and persist) the seeded personas so the onboarding personas
      // stage and the workspace personas tab populate without a live model.
      // Onboarding creates a fresh workspace id, so fall back to the canonical
      // dev_ws_personas_ws-dev-1 set (re-homed to this workspace) when empty.
      let personas = JSON.parse(localStorage.getItem(`dev_ws_personas_${workspaceId}`) || '[]');
      if (personas.length === 0) {
        const fallback = JSON.parse(localStorage.getItem('dev_ws_personas_ws-dev-1') || '[]');
        personas = fallback.map((p: any, i: number) => ({
          ...p,
          id: `${workspaceId}-persona-${i + 1}`,
          workspace_id: workspaceId,
        }));
        try {
          localStorage.setItem(`dev_ws_personas_${workspaceId}`, JSON.stringify(personas));
        } catch {
          // ignore
        }
      }
      return { personas, count: personas.length };
    }
    const response = await this.client.post(`/workspaces/${workspaceId}/personas/generate`, data);
    return response.data;
  }

  async updatePersona(workspaceId: string, personaId: string, data: import('@/types').PersonaUpdateRequest): Promise<import('@/types').WorkspacePersona> {
    const response = await this.client.put(`/workspaces/${workspaceId}/personas/${personaId}`, data);
    return response.data;
  }

  async deletePersona(workspaceId: string, personaId: string): Promise<void> {
    await this.client.delete(`/workspaces/${workspaceId}/personas/${personaId}`);
  }

  async setPrimaryPersona(workspaceId: string, personaId: string): Promise<import('@/types').WorkspacePersona> {
    const response = await this.client.post(`/workspaces/${workspaceId}/personas/${personaId}/primary`);
    return response.data;
  }

  // ============================================
  // Prompt Library endpoints
  // ============================================

  async getPromptLimits(workspaceId?: string): Promise<import('@/types').PromptLimitsResponse> {
    if (DEV_MODE) {
      await delay(200);
      const prompts = JSON.parse(localStorage.getItem('dev_prompts') || '[]');
      return {
        current_count: prompts.length,
        max_allowed: 50,
        can_save_more: prompts.length < 50,
        tier: 'pro',
      };
    }
    const params = workspaceId ? { workspace_id: workspaceId } : {};
    const response = await this.client.get('/prompts/limits', { params });
    return response.data;
  }

  async getPrompts(workspaceId?: string): Promise<import('@/types').PromptListResponse> {
    if (DEV_MODE) {
      await delay(200);
      const prompts = JSON.parse(localStorage.getItem('dev_prompts') || '[]');
      return {
        prompts,
        total_count: prompts.length,
        limit: 50,
        can_save_more: prompts.length < 50,
      };
    }
    const params = workspaceId ? { workspace_id: workspaceId } : {};
    const response = await this.client.get('/prompts', { params });
    return response.data;
  }

  async savePrompt(prompt_text: string, workspaceId?: string): Promise<import('@/types').SavedPrompt> {
    if (DEV_MODE) {
      await delay(300);
      const prompts = JSON.parse(localStorage.getItem('dev_prompts') || '[]');
      
      // Check for duplicates
      const exists = prompts.some((p: any) => p.prompt_text === prompt_text);
      if (exists) {
        throw { response: { data: { detail: 'This prompt already exists in your library.' } } };
      }
      
      // Generate display name (first 5 words)
      const words = prompt_text.split(/\s+/);
      const displayName = words.length <= 5 
        ? prompt_text.substring(0, 100)
        : words.slice(0, 5).join(' ') + '...';
      
      const newPrompt: import('@/types').SavedPrompt = {
        id: 'prompt-' + Date.now(),
        prompt_text,
        display_name: displayName,
        display_order: prompts.length,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      
      prompts.push(newPrompt);
      localStorage.setItem('dev_prompts', JSON.stringify(prompts));
      return newPrompt;
    }
    const params = workspaceId ? { workspace_id: workspaceId } : {};
    const response = await this.client.post('/prompts', { prompt_text }, { params });
    return response.data;
  }

  async updatePrompt(id: string, prompt_text: string, workspaceId?: string): Promise<import('@/types').SavedPrompt> {
    if (DEV_MODE) {
      await delay(300);
      const prompts = JSON.parse(localStorage.getItem('dev_prompts') || '[]');
      const index = prompts.findIndex((p: any) => p.id === id);
      if (index === -1) throw new Error('Prompt not found');
      
      prompts[index].prompt_text = prompt_text;
      // Auto-generate display name from first 5 words
      const words = prompt_text.split(/\s+/);
      prompts[index].display_name = words.length <= 5 
        ? prompt_text.substring(0, 100)
        : words.slice(0, 5).join(' ') + '...';
      prompts[index].updated_at = new Date().toISOString();
      localStorage.setItem('dev_prompts', JSON.stringify(prompts));
      return prompts[index];
    }
    const params = workspaceId ? { workspace_id: workspaceId } : {};
    const response = await this.client.put(`/prompts/${id}`, { prompt_text }, { params });
    return response.data;
  }

  async deletePrompt(id: string, workspaceId?: string): Promise<MessageResponse> {
    if (DEV_MODE) {
      await delay(300);
      const prompts = JSON.parse(localStorage.getItem('dev_prompts') || '[]');
      const filtered = prompts.filter((p: any) => p.id !== id);
      // Reorder
      filtered.forEach((p: any, i: number) => { p.display_order = i; });
      localStorage.setItem('dev_prompts', JSON.stringify(filtered));
      return { message: 'Prompt deleted successfully.' };
    }
    const params = workspaceId ? { workspace_id: workspaceId } : {};
    const response = await this.client.delete(`/prompts/${id}`, { params });
    return response.data;
  }

  async reorderPrompts(prompt_ids: string[], workspaceId?: string): Promise<import('@/types').SavedPrompt[]> {
    if (DEV_MODE) {
      await delay(300);
      const prompts = JSON.parse(localStorage.getItem('dev_prompts') || '[]');
      const reordered = prompt_ids.map((id, i) => {
        const prompt = prompts.find((p: any) => p.id === id);
        if (prompt) return { ...prompt, display_order: i };
        return null;
      }).filter(Boolean);
      localStorage.setItem('dev_prompts', JSON.stringify(reordered));
      return reordered;
    }
    const params = workspaceId ? { workspace_id: workspaceId } : {};
    const response = await this.client.put('/prompts/reorder', { prompt_ids }, { params });
    return response.data;
  }

  async checkPromptDuplicate(prompt_text: string): Promise<{ exists: boolean }> {
    if (DEV_MODE) {
      await delay(100);
      const prompts = JSON.parse(localStorage.getItem('dev_prompts') || '[]');
      const exists = prompts.some((p: any) => p.prompt_text === prompt_text);
      return { exists };
    }
    const response = await this.client.post('/prompts/check-duplicate', { prompt_text });
    return response.data;
  }

  // ============================================
  // AI Image Generation (Nano Banana Pro)
  // ============================================

  async generateAIImage(request: import('@/types').AIImageGenerateRequest): Promise<import('@/types').AIImageGenerateResponse> {
    if (DEV_MODE) {
      // Real image generation is 10-30s, not 2s.
      await delay(14000);
      await devFault('generateAIImage');
      // In dev mode, return a mock response
      return {
        image: {
          image_base64: null,
          image_prompt: 'Mock prompt for dev mode',
          mime_type: null,
          success: false,
          error: 'AI image generation is not available in dev mode. Please connect to the backend.',
        },
        remaining_generations: 30,
        max_edits_allowed: 3,
      };
    }
    const response = await this.client.post('/ai-image/generate', request);
    return response.data;
  }

  async cacheAIImage(platform: string, image: { image_base64: string; image_prompt: string; mime_type?: string; edit_count?: number }): Promise<void> {
    if (DEV_MODE) return;
    try {
      await this.client.post('/ai-image/cache', {
        platform,
        image_base64: image.image_base64,
        image_prompt: image.image_prompt,
        mime_type: image.mime_type || 'image/png',
        edit_count: image.edit_count || 0,
      });
    } catch {
      // Non-critical — silently ignore cache failures
    }
  }

  async checkRoastStatus(jobId: string): Promise<import('@/types').RoastStatusResponse> {
    const response = await this.client.get(`/ai-image/roast-status/${jobId}`);
    return response.data;
  }

  async editAIImage(request: import('@/types').AIImageEditRequest): Promise<import('@/types').AIImageGenerateResponse> {
    if (DEV_MODE) {
      await delay(2000);
      return {
        image: {
          image_base64: null,
          image_prompt: 'Mock edited prompt for dev mode',
          mime_type: null,
          success: false,
          error: 'AI image editing is not available in dev mode. Please connect to the backend.',
        },
        remaining_generations: 30,
        max_edits_allowed: 2,
      };
    }
    const response = await this.client.post('/ai-image/edit', request);
    return response.data;
  }

  // ============================================
  // AI Carousel
  // ============================================

  async planCarousel(request: { text: string; platform: string; post_content?: string; slide_count: number; service_id?: string; workspace_id?: string }): Promise<any> {
    const response = await this.client.post('/ai-image/carousel/plan', request);
    return response.data;
  }

  async generateCarousel(request: { text: string; platform: string; post_content?: string; slide_plans: any[]; service_id?: string; image_style_preference?: string; workspace_id?: string }): Promise<any> {
    const response = await this.client.post('/ai-image/carousel/generate', request, {
      timeout: 300000,
    });
    return response.data;
  }

  async generateSingleCarouselSlide(request: {
    text: string; platform: string; post_content?: string;
    slide_plan: any; total_slides: number; narrative_arc: string;
    service_id?: string; image_style_preference?: string; workspace_id?: string;
  }): Promise<any> {
    const attempt = async () => {
      const response = await this.client.post('/ai-image/carousel/slide/generate', request, {
        timeout: 180000, // 3 minutes per slide — prompt generation + image generation
      });
      return response.data;
    };
    try {
      return await attempt();
    } catch (err: any) {
      // Retry once on timeout
      if (err?.code === 'ECONNABORTED' || err?.message?.includes('timeout')) {
        return await attempt();
      }
      throw err;
    }
  }

  async consumeCarouselCredit(): Promise<{ remaining_carousels: number }> {
    const response = await this.client.post('/ai-image/carousel/consume-credit');
    return response.data;
  }

  async editCarouselSlide(request: { slide_number: number; edit_instruction: string; original_prompt: string; platform: string; previous_image_base64?: string; workspace_id?: string }): Promise<any> {
    const response = await this.client.post('/ai-image/carousel/slide/edit', request);
    return response.data;
  }

  async getCarouselLimits(): Promise<{ carousels_per_month: number; carousels_used: number; carousels_remaining: number; can_use_carousels: boolean }> {
    const response = await this.client.get('/ai-image/carousel/limits');
    return response.data;
  }

  // ============================================
  // Razorpay Payments
  // ============================================

  async getRazorpayConfig(): Promise<{
    key_id: string;
    currency: string;
    name: string;
    description: string;
    theme_color: string;
  }> {
    if (DEV_MODE) {
      return {
        key_id: 'rzp_test_demo',
        currency: 'INR',
        name: 'Zupa',
        description: 'Psychology-Powered Content Generation',
        theme_color: '#4F46E5',
      };
    }
    const response = await this.client.get('/payments/config');
    return response.data;
  }

  async getPaymentPricing(): Promise<{
    currency: string;
    currency_symbol: string;
    basic: { monthly: number; quarterly: number; yearly: number; monthly_display: string; quarterly_display: string; yearly_display: string };
    basic_plus: { monthly: number; quarterly: number; yearly: number; monthly_display: string; quarterly_display: string; yearly_display: string };
    pro: { monthly: number; quarterly: number; yearly: number; monthly_display: string; quarterly_display: string; yearly_display: string };
  }> {
    const response = await this.client.get('/payments/pricing');
    return response.data;
  }

  // ============================================
  // Razorpay Subscriptions
  // ============================================

  async validateCoupon(code: string): Promise<{ valid: boolean; message: string }> {
    const response = await this.client.post('/payments/validate-coupon', null, { params: { code } });
    return response.data;
  }

  async createSubscription(tier: string, billing_cycle: string = 'monthly', coupon_code?: string): Promise<{
    subscription_id: string;
    plan_id: string;
    tier: string;
    billing_cycle: string;
    plan_name: string;
    amount: number;
    amount_display: number;
    first_payment_amount: number;
    first_payment_display: number;
    currency: string;
    key_id: string;
    user_email: string;
    user_name: string;
    customer_id: string;
    short_url?: string;
    status: string;
    is_upgrade: boolean;
    proration_details?: any;
  }> {
    const payload: any = { tier, billing_cycle };
    if (coupon_code) payload.coupon_code = coupon_code;
    const response = await this.client.post('/payments/create-subscription', payload);
    return response.data;
  }

  async verifySubscription(data: {
    razorpay_subscription_id: string;
    razorpay_payment_id: string;
    razorpay_signature: string;
    tier: string;
    billing_cycle: string;
  }): Promise<{
    success: boolean;
    message: string;
    tier?: string;
    subscription_ends_at?: string;
    subscription_status?: string;
  }> {
    const response = await this.client.post('/payments/verify-subscription', data);
    return response.data;
  }

  async getSubscriptionStatus(): Promise<{
    has_subscription: boolean;
    subscription_id?: string;
    subscription_status?: string;
    tier: string;
    billing_cycle?: string;
    current_period_start?: string;
    current_period_end?: string;
    cancel_at_period_end: boolean;
    pending_tier?: string;
  }> {
    const response = await this.client.get('/payments/subscription-status');
    return response.data;
  }

  async cancelSubscription(cancelImmediately: boolean = false, reason?: string): Promise<{
    success: boolean;
    message: string;
    cancel_immediately: boolean;
    subscription_ends_at?: string;
  }> {
    const response = await this.client.post('/payments/cancel-subscription', {
      cancel_immediately: cancelImmediately,
      reason,
    });
    return response.data;
  }

  async scheduleDowngrade(targetTier: string): Promise<{
    success: boolean;
    message: string;
    current_tier: string;
    pending_tier: string;
    effective_date?: string;
  }> {
    const response = await this.client.post('/payments/schedule-downgrade', {
      target_tier: targetTier,
    });
    return response.data;
  }

  async cancelScheduledDowngrade(): Promise<{
    success: boolean;
    message: string;
  }> {
    const response = await this.client.post('/payments/cancel-downgrade');
    return response.data;
  }

  async previewUpgradePrice(tier: string, billing_cycle: string = 'monthly'): Promise<{
    tier: string;
    billing_cycle: string;
    is_upgrade: boolean;
    current_tier: string;
    original_price: number;
    original_price_display: string;
    final_price: number;
    final_price_display: string;
    discount: number;
    discount_display: string;
    proration_details?: any;
    message: string;
  }> {
    const response = await this.client.get('/payments/preview-upgrade', {
      params: { tier, billing_cycle },
    });
    return response.data;
  }

  async changeTier(targetTier: string, confirm: boolean = false): Promise<{
    success: boolean;
    message: string;
    old_tier: string;
    new_tier: string;
    features_lost: string[];
    data_affected?: string;
    requires_confirmation?: boolean;
  }> {
    const response = await this.client.post('/auth/subscription/change-tier', {
      target_tier: targetTier,
      confirm,
    });
    return response.data;
  }

  // ============================================
  // Feedback
  // ============================================

  async submitFeedback(feedback: string): Promise<{
    success: boolean;
    message: string;
  }> {
    const response = await this.client.post('/feedback', { feedback });
    return response.data;
  }

  async submitPostFeedback(data: {
    feedback: string;
    prompt: string;
    generated_posts: Record<string, string[]>;
    rating?: number;
  }): Promise<{
    success: boolean;
    message: string;
  }> {
    const response = await this.client.post('/feedback/post', data);
    return response.data;
  }

  // ============================================
  // Trial/Fingerprint endpoints
  // ============================================

  async submitFingerprint(fingerprint: string): Promise<{
    status: 'approved' | 'pending';
    message: string;
  }> {
    if (DEV_MODE) {
      await delay(500);
      return { status: 'approved', message: 'Trial approved (dev mode)' };
    }
    const response = await this.client.post('/auth/submit-fingerprint', { fingerprint });
    return response.data;
  }

  async getTrialStatus(): Promise<{
    trial_status: string;
    can_access_app: boolean;
    message: string;
  }> {
    if (DEV_MODE) {
      await delay(200);
      return {
        trial_status: 'auto_approved',
        can_access_app: true,
        message: 'Trial active (dev mode)',
      };
    }
    const response = await this.client.get('/auth/trial-status');
    return response.data;
  }

  // ============================================
  // Admin endpoints
  // ============================================

  async getTrialRequests(
    statusFilter?: string,
    page: number = 1,
    perPage: number = 20
  ): Promise<{
    requests: Array<{
      id: string;
      email: string;
      full_name: string | null;
      trial_status: string;
      created_at: string;
      is_verified: boolean;
      is_flagged: boolean;
      device_fingerprint_short: string | null;
    }>;
    total_count: number;
    pending_count: number;
  }> {
    if (DEV_MODE) {
      await delay(300);
      return { requests: [], total_count: 0, pending_count: 0 };
    }
    const response = await this.client.get('/admin/trial-requests', {
      params: {
        status_filter: statusFilter,
        page,
        per_page: perPage,
      },
    });
    return response.data;
  }

  async getTrialRequestDetail(userId: string): Promise<{
    user: {
      id: string;
      email: string;
      normalized_email: string | null;
      full_name: string | null;
      trial_status: string;
      created_at: string | null;
      is_verified: boolean;
      trial_reviewed_at: string | null;
      trial_reviewed_by: string | null;
      rejection_reason: string | null;
    };
    fingerprint_info: {
      hash: string | null;
      hash_short: string | null;
      other_accounts_count: number;
      other_accounts: Array<{
        id: string;
        email: string;
        full_name: string | null;
        trial_status: string;
        created_at: string | null;
        is_verified: boolean;
      }>;
      registration_count?: number;
      first_seen?: string | null;
    };
  }> {
    if (DEV_MODE) {
      throw new Error('Admin features not available in dev mode');
    }
    const response = await this.client.get(`/admin/trial-requests/${userId}`);
    return response.data;
  }

  async approveTrialRequest(userId: string): Promise<{
    message: string;
  }> {
    if (DEV_MODE) {
      throw new Error('Admin features not available in dev mode');
    }
    const response = await this.client.post(`/admin/trial-requests/${userId}/approve`);
    return response.data;
  }

  async rejectTrialRequest(userId: string, reason?: string): Promise<{
    message: string;
  }> {
    if (DEV_MODE) {
      throw new Error('Admin features not available in dev mode');
    }
    const response = await this.client.post(`/admin/trial-requests/${userId}/reject`, {
      reason,
    });
    return response.data;
  }

  async getAdminStats(): Promise<{
    total_users: number;
    pending_reviews: number;
    approved_trials: number;
    rejected_trials: number;
    auto_approved_trials: number;
    unique_fingerprints: number;
    flagged_fingerprints: number;
    recent_registrations: number;
  }> {
    if (DEV_MODE) {
      await delay(200);
      return {
        total_users: 0,
        pending_reviews: 0,
        approved_trials: 0,
        rejected_trials: 0,
        auto_approved_trials: 0,
        unique_fingerprints: 0,
        flagged_fingerprints: 0,
        recent_registrations: 0,
      };
    }
    const response = await this.client.get('/admin/stats');
    return response.data;
  }

  async getAdminDashboard(): Promise<import('@/types').AdminDashboardData> {
    const response = await this.client.get('/admin/dashboard');
    return response.data;
  }

  async getAdminUsers(params?: {
    search?: string;
    tier?: string;
    subscription_status?: string;
    paying?: boolean;
    verified?: boolean;
    trial_status?: string;
    sort_by?: string;
    sort_order?: string;
    page?: number;
    per_page?: number;
  }): Promise<import('@/types').AdminUsersResponse> {
    const response = await this.client.get('/admin/users', { params });
    return response.data;
  }

  async getAdminInternalAdmins(): Promise<{
    admins: {
      id: string;
      email: string;
      full_name: string | null;
      is_verified: boolean;
      generation_count_this_month: number;
      ai_image_count_this_month: number;
      last_activity_at: string | null;
      created_at: string | null;
    }[];
    unregistered_emails: string[];
    total_configured: number;
  }> {
    const response = await this.client.get('/admin/internal-admins');
    return response.data;
  }

  // ============================================
  // Dashboard
  // ============================================

  async getDashboardStats(): Promise<import('@/types').DashboardStats> {
    if (DEV_MODE) {
      await delay(200);
      await devFault('getDashboardStats');
      // DERIVED from the seeded posts — never fabricated. This block used to
      // hardcode `total_posts: 42` while getPosts returned an empty list, so
      // /dashboard celebrated "SHIPPED 42 POSTS, you've saved ~11h and ₹21,000"
      // while /posts said "0 posts generated": the same app, the same session,
      // contradicting itself. Delete a post now and this drops by one.
      return deriveDashboardStats(devPosts());
    }
    const response = await this.client.get('/dashboard/stats');
    return response.data;
  }

  // ============================================
  // Calendar Notes
  // ============================================

  // ============================================
  // Content Ideas
  // ============================================

  async getContentIdeas(): Promise<import('@/types').ContentIdeasResponse> {
    if (DEV_MODE) {
      await delay(150);
      if (await devFault('getContentIdeas')) {
        return {
          ideas: [],
          workspace_id: 'ws-dev-1',
          generated_at: new Date().toISOString(),
          expires_at: new Date(Date.now() + 86400000).toISOString(),
          refreshes_remaining: 3,
        };
      }
      // Real-shaped ideas, not an empty list. This used to return `ideas: []`,
      // which meant Content Ideas could NEVER render in dev — so the feature was
      // invisible to anyone testing locally, and impossible to verify.
      return {
        ideas: DEV_CONTENT_IDEAS,
        workspace_id: 'ws-dev-1',
        generated_at: new Date().toISOString(),
        expires_at: new Date(Date.now() + 86400000).toISOString(),
        refreshes_remaining: 3,
      };
    }
    const response = await this.client.get('/content-ideas');
    return response.data;
  }

  async refreshContentIdeas(): Promise<import('@/types').ContentIdeasResponse> {
    if (DEV_MODE) {
      // A real refresh is a model call, and it must come back with IDEAS —
      // this returned `ideas: []`, so hitting Refresh in dev emptied the panel
      // and made a working feature look broken.
      await delay(4000);
      return {
        ideas: [...DEV_CONTENT_IDEAS].reverse(),
        workspace_id: 'ws-dev-1',
        generated_at: new Date().toISOString(),
        expires_at: new Date(Date.now() + 86400000).toISOString(),
        refreshes_remaining: 2,
      };
    }
    const response = await this.client.post('/content-ideas/refresh');
    return response.data;
  }

  // ============================================
  // Calendar Notes
  // ============================================

  async getCalendarNotes(year: number, month: number): Promise<import('@/types').CalendarNote[]> {
    if (DEV_MODE) {
      await delay(150);
      return JSON.parse(localStorage.getItem(`dev_calendar_notes_${year}_${month}`) || '[]');
    }
    const response = await this.client.get(`/calendar-notes/${year}/${month}`);
    return response.data;
  }

  async upsertCalendarNote(date: string, content: string): Promise<import('@/types').CalendarNote> {
    const response = await this.client.post(`/calendar-notes/${date}`, { content });
    return response.data;
  }

  async deleteCalendarNote(date: string): Promise<void> {
    await this.client.delete(`/calendar-notes/${date}`);
  }

  // ============================================
  // Case Studies
  // ============================================

  async generateCaseStudy(
    input: import('@/types').CaseStudyGenerationInput,
    workspaceId?: string,
  ): Promise<import('@/types').CaseStudyResponse> {
    if (DEV_MODE) {
      // The case-study engine is a long Claude call — tens of seconds. No dev
      // branch existed at all, so "Generate" simply threw in dev.
      await delay(20000);
      await devFault('generateCaseStudy');
      const detail = buildDevCaseStudyDetail('cs-dev-1');
      return { ...detail, id: 'cs-' + Date.now(), input, created_at: new Date().toISOString() };
    }
    const response = await this.client.post('/case-studies/generate', {
      input,
      workspace_id: workspaceId,
    });
    return response.data;
  }

  async listCaseStudies(workspaceId?: string): Promise<import('@/types').CaseStudyListItem[]> {
    if (DEV_MODE) {
      await delay(150);
      if (await devFault('listCaseStudies')) return [];
      // Two generated + one draft, so both list states render.
      return buildDevCaseStudies();
    }
    const params = workspaceId ? { workspace_id: workspaceId } : {};
    const response = await this.client.get('/case-studies', { params });
    return response.data;
  }

  async getCaseStudy(id: string, workspaceId?: string): Promise<import('@/types').CaseStudyDetailResponse> {
    if (DEV_MODE) {
      await delay(200);
      await devFault('getCaseStudy');
      // This branch is new. Seeding the LIST without it would have moved the
      // dead end one click deeper: the library would populate and every row
      // would 404 against a backend that isn't there.
      return buildDevCaseStudyDetail(id);
    }
    const params = workspaceId ? { workspace_id: workspaceId } : {};
    const response = await this.client.get(`/case-studies/${id}`, { params });
    return response.data;
  }

  async runCaseStudyCampaign(
    id: string,
    workspaceId?: string,
    platforms?: string[],
  ): Promise<import('@/types').CampaignRunResponse> {
    if (DEV_MODE) {
      await delay(12000);
      await devFault('runCaseStudyCampaign');
      const entries = buildDevCaseStudyDetail(id).campaign_entries;
      return { case_study_id: id, count: entries.length, entries };
    }
    const body: Record<string, any> = {};
    if (workspaceId) body.workspace_id = workspaceId;
    if (platforms) body.platforms = platforms;
    const response = await this.client.post(`/case-studies/${id}/campaign`, body);
    return response.data;
  }

  async getCampaignCalendarEntries(
    year: number,
    month: number,
    workspaceId?: string,
  ): Promise<import('@/types').CampaignEntryOut[]> {
    if (DEV_MODE) {
      await delay(150);
      if (await devFault('getCampaignCalendarEntries')) return [];
      // Filtered by the requested month, not returned wholesale — a calendar
      // mock that ignores year/month makes broken month navigation look fine.
      return buildDevCampaignCalendar(year, month);
    }
    const params = workspaceId ? { workspace_id: workspaceId } : {};
    const response = await this.client.get(`/case-studies/calendar/${year}/${month}`, { params });
    return response.data;
  }

  // These three had NO DEV_MODE branch, so /case-studies fired three requests at
  // a backend that isn't there and logged 500s on every load. A missing dev
  // branch is the same lie as an empty one, just louder.
  async getWorkspaceKeywords(workspaceId?: string): Promise<import('@/types').WorkspaceKeyword[]> {
    if (DEV_MODE) {
      await delay(150);
      return devRead<import('@/types').WorkspaceKeyword[]>(DEV_KEYS.keywords, []);
    }
    const params = workspaceId ? { workspace_id: workspaceId } : {};
    const response = await this.client.get('/case-studies/keywords', { params });
    return response.data.keywords;
  }

  async addWorkspaceKeyword(keyword: string, workspaceId?: string): Promise<import('@/types').WorkspaceKeyword[]> {
    if (DEV_MODE) {
      await delay(200);
      const kws = devRead<import('@/types').WorkspaceKeyword[]>(DEV_KEYS.keywords, []);
      if (!kws.some((k) => k.term === keyword)) {
        kws.push({ term: keyword, source: 'custom', recently_used: false });
        devWrite(DEV_KEYS.keywords, kws);
      }
      return kws;
    }
    const body: Record<string, any> = { keyword };
    if (workspaceId) body.workspace_id = workspaceId;
    const response = await this.client.post('/case-studies/keywords', body);
    return response.data.keywords;
  }

  async removeWorkspaceKeyword(keyword: string, workspaceId?: string): Promise<import('@/types').WorkspaceKeyword[]> {
    if (DEV_MODE) {
      await delay(200);
      const kws = devRead<import('@/types').WorkspaceKeyword[]>(DEV_KEYS.keywords, [])
        .filter((k) => k.term !== keyword);
      devWrite(DEV_KEYS.keywords, kws);
      return kws;
    }
    const params: Record<string, any> = { keyword };
    if (workspaceId) params.workspace_id = workspaceId;
    const response = await this.client.delete('/case-studies/keywords', { params });
    return response.data.keywords;
  }

  // ============================================
  // Generation History
  // ============================================

  async saveGenerationSession(workspaceId: string, data: import('@/types').SaveSessionRequest): Promise<import('@/types').SaveSessionResponse> {
    if (DEV_MODE) {
      await delay(200);
      const sessions = devRead<import('@/types').GenerationSessionSummary[]>(DEV_KEYS.sessions, []);
      const now = new Date().toISOString();

      // A regenerate carries the existing session_id and is a new TURN on that
      // session — not a new session. Creating a fresh row each time would make
      // History fill up with duplicates and hide the turn-count behaviour.
      const existing = data.session_id
        ? sessions.findIndex((s) => s.id === data.session_id)
        : -1;
      if (existing !== -1) {
        const turn = sessions[existing].turn_count + 1;
        sessions[existing] = { ...sessions[existing], turn_count: turn, updated_at: now };
        devWrite(DEV_KEYS.sessions, sessions);
        return {
          session_id: sessions[existing].id,
          turn_id: `${sessions[existing].id}-turn-${turn}`,
          turn_number: turn,
        };
      }

      // A generation you just ran has to show up in History, or the panel is
      // decorative.
      const id = 'sess-' + Date.now();
      devWrite(DEV_KEYS.sessions, [
        {
          id,
          title: (data.prompt_text || 'Untitled generation').slice(0, 60),
          prompt_text: data.prompt_text || '',
          platforms: data.platforms || [],
          turn_count: 1,
          created_at: now,
          updated_at: now,
        },
        ...sessions,
      ]);
      return { session_id: id, turn_id: `${id}-turn-1`, turn_number: 1 };
    }
    const response = await this.client.post(`/history/workspaces/${workspaceId}/sessions`, data);
    return response.data;
  }

  async getGenerationSessions(workspaceId: string, page = 1, perPage = 20, search?: string): Promise<import('@/types').SessionListResponse> {
    if (DEV_MODE) {
      await delay(150);
      if (await devFault('getGenerationSessions')) return { sessions: [], has_more: false, total: 0 };
      // Seeded history — the tool sidebar used to say "Your generation history
      // will appear here" forever, because this returned a hardcoded empty list.
      const needle = search?.trim().toLowerCase();
      const all = devRead<import('@/types').GenerationSessionSummary[]>(DEV_KEYS.sessions, [])
        .filter((s) =>
          needle
            ? s.title.toLowerCase().includes(needle) || s.prompt_text.toLowerCase().includes(needle)
            : true,
        )
        .sort((a, b) => Date.parse(b.updated_at) - Date.parse(a.updated_at));
      const start = (page - 1) * perPage;
      const slice = all.slice(start, start + perPage);
      return { sessions: slice, has_more: start + slice.length < all.length, total: all.length };
    }
    const params: Record<string, any> = { page, per_page: perPage };
    if (search) params.search = search;
    const response = await this.client.get(`/history/workspaces/${workspaceId}/sessions`, { params });
    return response.data;
  }

  async getGenerationSession(workspaceId: string, sessionId: string): Promise<import('@/types').GenerationSessionDetail> {
    if (DEV_MODE) {
      await delay(200);
      await devFault('getGenerationSession');
      // This branch is new. Seeding the history LIST without it would mean every
      // row rendered and none of them opened.
      return buildDevSessionDetail(sessionId);
    }
    const response = await this.client.get(`/history/workspaces/${workspaceId}/sessions/${sessionId}`);
    return response.data;
  }

  async deleteGenerationSession(workspaceId: string, sessionId: string): Promise<void> {
    if (DEV_MODE) {
      await delay(200);
      const sessions = devRead<import('@/types').GenerationSessionSummary[]>(DEV_KEYS.sessions, []);
      devWrite(DEV_KEYS.sessions, sessions.filter((s) => s.id !== sessionId));
      return;
    }
    await this.client.delete(`/history/workspaces/${workspaceId}/sessions/${sessionId}`);
  }

  async renameGenerationSession(workspaceId: string, sessionId: string, title: string): Promise<import('@/types').GenerationSessionSummary> {
    if (DEV_MODE) {
      await delay(200);
      const sessions = devRead<import('@/types').GenerationSessionSummary[]>(DEV_KEYS.sessions, []);
      const idx = sessions.findIndex((s) => s.id === sessionId);
      if (idx === -1) throw new Error('Session not found');
      sessions[idx] = { ...sessions[idx], title, updated_at: new Date().toISOString() };
      devWrite(DEV_KEYS.sessions, sessions);
      return sessions[idx];
    }
    const response = await this.client.patch(`/history/workspaces/${workspaceId}/sessions/${sessionId}`, { title });
    return response.data;
  }

  // ============================================
  // Autopilot Calendar
  // ============================================

  async getPendingAutopilot(workspaceId?: string): Promise<import('@/types').AutopilotEntry[]> {
    if (DEV_MODE) {
      await delay(150);
      if (await devFault('getPendingAutopilot')) return [];
      // The same drafts the Feed shows — the calendar-side view of one queue,
      // not a second, contradicting one.
      return buildDevPendingAutopilot();
    }
    const params = workspaceId ? { workspace_id: workspaceId } : {};
    const response = await this.client.get('/autopilot/pending', { params });
    return response.data;
  }

  // Streams a month of autopilot drafts over SSE — each draft arrives via onDraft the moment
  // it is generated, so a long month (up to ~8 topics x N platforms x ~18s) keeps the
  // connection alive and never hits Cloudflare's ~100s 524. Generates one draft per
  // (topic x platform) for the chosen platforms. Returns an abort handle.
  generateAutopilotMonthStream(
    workspaceId: string | undefined,
    platforms: string[],
    callbacks: {
      onStarted?: () => void;
      onDraft?: (draft: import('@/types').AutopilotEntry) => void;
      onComplete?: (info: { count: number }) => void;
      onError?: (error: string) => void;
    }
  ): { abort: () => void } {
    const abortController = new AbortController();
    const chosen = platforms && platforms.length ? platforms : ['linkedin'];

    if (DEV_MODE) {
      (async () => {
        callbacks.onStarted?.();
        let n = 0;
        for (let t = 1; t <= 2; t++) {
          for (const platform of chosen) {
            await delay(400);
            n += 1;
            callbacks.onDraft?.({
              id: `dev-${n}`,
              case_study_id: null,
              date: new Date().toISOString().slice(0, 10),
              platform,
              content: `Dev autopilot draft #${n} (${platform}) from the monthly brief.`,
              kind: 'autopilot_post',
              status: 'pending_approval',
            } as import('@/types').AutopilotEntry);
          }
        }
        callbacks.onComplete?.({ count: n });
      })();
      return { abort: () => abortController.abort() };
    }

    const token = localStorage.getItem('access_token');
    const qs = new URLSearchParams();
    if (workspaceId) qs.set('workspace_id', workspaceId);
    chosen.forEach((p) => qs.append('platforms', p));
    const params = `?${qs.toString()}`;
    let completeCalled = false;

    const processSSEText = (text: string) => {
      for (const block of text.split('\n\n')) {
        if (!block.trim()) continue;
        let eventType = '';
        let eventData = '';
        for (const line of block.split('\n')) {
          if (line.startsWith('event: ')) eventType = line.slice(7).trim();
          else if (line.startsWith('data: ')) eventData = line.slice(6);
        }
        if (!eventType || !eventData) continue;
        try {
          const parsed = JSON.parse(eventData);
          switch (eventType) {
            case 'started':
              callbacks.onStarted?.();
              break;
            case 'draft':
              callbacks.onDraft?.(parsed);
              break;
            case 'complete':
              completeCalled = true;
              callbacks.onComplete?.(parsed);
              break;
            case 'error':
              callbacks.onError?.(parsed.message);
              break;
          }
        } catch (e) {
          console.error('[autopilot SSE] parse failed:', eventData, e);
        }
      }
    };

    fetch(`${API_BASE_URL}/autopilot/generate${params}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      signal: abortController.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          const msg = typeof err.detail === 'string' ? err.detail : `HTTP ${response.status}`;
          callbacks.onError?.(msg);
          return;
        }
        const reader = response.body?.getReader();
        if (!reader) {
          callbacks.onError?.('No response body');
          return;
        }
        const decoder = new TextDecoder();
        let buffer = '';
        while (true) {
          const { done, value } = await reader.read();
          if (value) {
            buffer += decoder.decode(value, { stream: true });
            const lastSep = buffer.lastIndexOf('\n\n');
            if (lastSep !== -1) {
              processSSEText(buffer.substring(0, lastSep + 2));
              buffer = buffer.substring(lastSep + 2);
            }
          }
          if (done) {
            if (buffer.trim()) processSSEText(buffer);
            if (!completeCalled) callbacks.onComplete?.({ count: 0 });
            break;
          }
        }
      })
      .catch((error) => {
        if (error.name !== 'AbortError') callbacks.onError?.(error.message || 'Stream failed');
      });

    return { abort: () => abortController.abort() };
  }

  async getFeed(workspaceId?: string): Promise<import('@/types').AutopilotEntry[]> {
    if (DEV_MODE) {
      await delay(150);
      if (await devFault('getFeed')) return [];
      // The Feed is the logged-in front door and it was ALWAYS "Nothing waiting
      // for you right now" in dev. Seeded with three autopilot drafts and a
      // disruption alert — one of them deliberately flawed (unverifiable
      // superlative + an unsupported dollar figure) so FeedCard's credibility
      // warning path is reachable at all.
      return devRead<import('@/types').AutopilotEntry[]>(DEV_KEYS.feed, buildDevFeed());
    }
    const params = workspaceId ? { workspace_id: workspaceId } : {};
    const response = await this.client.get('/autopilot/feed', { params });
    return response.data;
  }

  /**
   * Poll for the image generated when an autopilot / disruption draft is approved.
   *
   * Approve used to BLOCK on this image call — 30-105s of Gemini inside a PATCH, which is why
   * the button span forever and died at Cloudflare's ~100s. The image is now generated in a
   * background task and handed over via Redis, so approve returns immediately and the client
   * collects the image here.
   *
   * `pending` is the ONLY non-terminal state. `ready`, `failed` and `none` all mean stop.
   * Poll with your own give-up timeout: if the worker dies mid-generation the key stays
   * `pending` until its TTL, so a terminal state is not guaranteed to arrive.
   */
  async getApprovalImage(
    entryId: string,
    kind: 'autopilot_post' | 'disruption_post' | string,
    workspaceId?: string,
  ): Promise<{
    status: 'pending' | 'ready' | 'failed' | 'none';
    image_base64: string | null;
    image_mime_type: string | null;
    reason: string | null;
  }> {
    const base = kind === 'disruption_post' ? 'disruption' : 'autopilot';
    if (DEV_MODE) {
      await delay(1200);
      // No real image pipeline in dev. Say so honestly rather than leaving a poller
      // spinning on a `pending` that will never resolve.
      return { status: 'none', image_base64: null, image_mime_type: null, reason: 'AI images are not generated in dev mode.' };
    }
    const params = workspaceId ? { workspace_id: workspaceId } : {};
    const response = await this.client.get(`/${base}/${entryId}/image`, { params });
    return response.data;
  }

  async updateAutopilotEntry(
    entryId: string,
    payload: { content?: string; action?: 'approve' | 'reject' },
    workspaceId?: string
  ): Promise<import('@/types').EntryUpdateResponse> {
    if (DEV_MODE) {
      await delay(150);
      return {
        id: entryId,
        case_study_id: null,
        date: new Date().toISOString().slice(0, 10),
        platform: 'linkedin',
        content: payload.content || '',
        kind: 'autopilot_post',
        status: payload.action || null,
      };
    }
    const params = workspaceId ? { workspace_id: workspaceId } : {};
    const response = await this.client.patch(`/autopilot/${entryId}`, payload, { params });
    return response.data;
  }

  async fanoutEntry(entryId: string, workspaceId?: string): Promise<import('@/types').FanoutResponse> {
    if (DEV_MODE) {
      await delay(300);
      return {
        entry_id: entryId,
        source_platform: 'linkedin',
        idempotent: false,
        model_calls: 2,
        assets: [
          { platform: 'linkedin', role: 'source', status: 'ok', content: 'The approved post.', first_comment: 'First comment for LinkedIn.', principle_id: 'framing', principle_label: 'Framing', why_line: 'Fits the angle.', hashtags: ['Logistics'], needs_review: false, post_id: 'dev-1', error: null },
          { platform: 'twitter', role: 'variant', status: 'ok', content: 'The approved post, adapted for X.', first_comment: 'First comment for X.', principle_id: 'framing', principle_label: 'Framing', why_line: 'Fits the angle.', hashtags: ['Ops'], needs_review: false, post_id: 'dev-2', error: null },
        ],
      };
    }
    const params = workspaceId ? { workspace_id: workspaceId } : {};
    const response = await this.client.post(`/autopilot/${entryId}/fanout`, null, { params });
    return response.data;
  }

  // ============================================
  // Disruption Alerts (Sprint 7)
  // ============================================

  async getPendingDisruptions(workspaceId?: string): Promise<import('@/types').AutopilotEntry[]> {
    if (DEV_MODE) {
      await delay(150);
      if (await devFault('getPendingDisruptions')) return [];
      return buildDevDisruptions();
    }
    const params = workspaceId ? { workspace_id: workspaceId } : {};
    const response = await this.client.get('/disruption/pending', { params });
    return response.data;
  }

  async checkDisruptionsNow(workspaceId?: string): Promise<import('@/types').AutopilotEntry[]> {
    if (DEV_MODE) {
      // A real scan hits the news/competitor sources — seconds, not 300ms.
      await delay(6000);
      if (await devFault('checkDisruptionsNow')) return [];
      // Returns a NEW alert, not the ones already pending: "check now" that
      // returns the same list every time can't tell you the button works.
      return buildFreshDisruption();
    }
    const params = workspaceId ? { workspace_id: workspaceId } : {};
    const response = await this.client.post('/disruption/check', null, { params });
    return response.data;
  }

  // ============================================
  // Drip Micro-Questions (Sprint 19)
  // ============================================

  async getDripQuestion(workspaceId?: string): Promise<import('@/types').DripQuestion | null> {
    if (DEV_MODE) {
      await delay(150);
      return null;
    }
    const params = workspaceId ? { workspace_id: workspaceId } : {};
    const response = await this.client.get('/drip/question', { params });
    return response.data.question;
  }

  async answerDrip(category: string, text: string, workspaceId?: string): Promise<{ ok: boolean; fact_id?: string }> {
    if (DEV_MODE) {
      await delay(150);
      return { ok: true };
    }
    const params = workspaceId ? { workspace_id: workspaceId } : {};
    const response = await this.client.post('/drip/answer', { category, text }, { params });
    return response.data;
  }

  async dismissDrip(category: string, workspaceId?: string): Promise<{ ok: boolean }> {
    if (DEV_MODE) {
      await delay(150);
      return { ok: true };
    }
    const params = workspaceId ? { workspace_id: workspaceId } : {};
    const response = await this.client.post('/drip/dismiss', { category }, { params });
    return response.data;
  }

  async updateDisruptionEntry(
    entryId: string,
    payload: { content?: string; action?: 'approve' | 'reject' },
    workspaceId?: string
  ): Promise<import('@/types').EntryUpdateResponse> {
    if (DEV_MODE) {
      await delay(150);
      return {
        id: entryId,
        case_study_id: null,
        date: new Date().toISOString().slice(0, 10),
        platform: 'linkedin',
        content: payload.content || '',
        kind: 'disruption_post',
        status: payload.action || null,
      };
    }
    const params = workspaceId ? { workspace_id: workspaceId } : {};
    const response = await this.client.patch(`/disruption/${entryId}`, payload, { params });
    return response.data;
  }

  // ============================================
  // Workspace Examples
  // ============================================

  async createWorkspaceExamples(
    workspaceId: string,
    items: import('@/types').WorkspaceExampleCreate[]
  ): Promise<import('@/types').WorkspaceExample[]> {
    if (DEV_MODE) {
      await delay(150);
      return items.map((item, idx) => ({
        id: `example-${idx}`,
        platform: item.platform || 'linkedin',
        content: item.content,
        created_at: new Date().toISOString(),
      }));
    }
    const response = await this.client.post(`/workspaces/${workspaceId}/examples`, items);
    return response.data;
  }

  async listWorkspaceExamples(workspaceId: string): Promise<import('@/types').WorkspaceExample[]> {
    if (DEV_MODE) {
      await delay(150);
      if (await devFault('listWorkspaceExamples')) return [];
      return buildDevExamples();
    }
    const response = await this.client.get(`/workspaces/${workspaceId}/examples`);
    return response.data;
  }

  async deleteWorkspaceExample(workspaceId: string, exampleId: string): Promise<void> {
    if (DEV_MODE) {
      await delay(150);
      return;
    }
    await this.client.delete(`/workspaces/${workspaceId}/examples/${exampleId}`);
  }

  // ============================================
  // Fact Bank (Sprint 17)
  // ============================================

  async getFacts(workspaceId: string): Promise<Fact[]> {
    if (DEV_MODE) {
      await delay(150);
      if (await devFault('getFacts')) return [];
      // Seeded with all three kinds (fact / stance / boundary) so every branch
      // of the Fact Bank's rendering is exercised, not just the one.
      return devRead<Fact[]>(DEV_KEYS.facts, []);
    }
    const response = await this.client.get(`/workspaces/${workspaceId}/facts`);
    return response.data;
  }

  async createFact(
    workspaceId: string,
    data: { kind: 'fact' | 'stance' | 'boundary'; text: string; category?: string; source?: string }
  ): Promise<Fact> {
    if (DEV_MODE) {
      await delay(150);
      const fact: Fact = {
        id: 'fact-' + Date.now(),
        kind: data.kind,
        text: data.text,
        category: data.category ?? null,
        source: data.source || 'user',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      devWrite(DEV_KEYS.facts, [fact, ...devRead<Fact[]>(DEV_KEYS.facts, [])]);
      return fact;
    }
    const response = await this.client.post(`/workspaces/${workspaceId}/facts`, data);
    return response.data;
  }

  async updateFact(
    workspaceId: string,
    factId: string,
    data: { text?: string; category?: string; kind?: 'fact' | 'stance' | 'boundary' }
  ): Promise<Fact> {
    if (DEV_MODE) {
      await delay(150);
      const facts = devRead<Fact[]>(DEV_KEYS.facts, []);
      const idx = facts.findIndex((f) => f.id === factId);
      if (idx === -1) throw new Error('Fact not found');
      facts[idx] = { ...facts[idx], ...data, updated_at: new Date().toISOString() };
      devWrite(DEV_KEYS.facts, facts);
      return facts[idx];
    }
    const response = await this.client.patch(`/workspaces/${workspaceId}/facts/${factId}`, data);
    return response.data;
  }

  async deleteFact(workspaceId: string, factId: string): Promise<void> {
    if (DEV_MODE) {
      await delay(150);
      devWrite(DEV_KEYS.facts, devRead<Fact[]>(DEV_KEYS.facts, []).filter((f) => f.id !== factId));
      return;
    }
    await this.client.delete(`/workspaces/${workspaceId}/facts/${factId}`);
  }

  async mineFacts(workspaceId: string): Promise<{ candidates: MineCandidate[] }> {
    if (DEV_MODE) {
      await delay(300);
      return { candidates: [] };
    }
    const response = await this.client.post(`/workspaces/${workspaceId}/facts/mine`);
    return response.data;
  }

  // ============================================
  // Fuel Gauge, Moments, Harvest (Sprint 25 — "The Hungry Bank")
  // ============================================

  async getFuel(workspaceId: string): Promise<import('@/types').FuelGauge> {
    if (DEV_MODE) {
      await delay(150);
      return devRead<import('@/types').FuelGauge>(DEV_FUEL_KEY, DEV_EMPTY_FUEL);
    }
    const response = await this.client.get(`/workspaces/${workspaceId}/fuel`);
    return response.data;
  }

  async createMoment(
    workspaceId: string,
    data: { text: string; occurred_at?: string | null }
  ): Promise<import('@/types').Moment> {
    if (DEV_MODE) {
      await delay(200);
      const fuel = devRead<import('@/types').FuelGauge>(DEV_FUEL_KEY, DEV_EMPTY_FUEL);
      devWrite(DEV_FUEL_KEY, {
        ...fuel,
        moment_fresh: fuel.moment_fresh + 1,
        moment_total: fuel.moment_total + 1,
      });
      return { id: 'moment-' + Date.now(), text: data.text, occurred_at: data.occurred_at ?? null };
    }
    const response = await this.client.post(`/workspaces/${workspaceId}/moments`, data);
    return response.data;
  }

  async getHarvestPending(workspaceId: string): Promise<import('@/types').BankItem[]> {
    if (DEV_MODE) {
      await delay(150);
      return devRead<import('@/types').BankItem[]>(DEV_HARVEST_PENDING_KEY, []);
    }
    const response = await this.client.get(`/workspaces/${workspaceId}/harvest/pending`);
    return response.data;
  }

  async harvestPaste(workspaceId: string, text: string): Promise<import('@/types').HarvestResult> {
    if (DEV_MODE) {
      await delay(400);
      // A rough stand-in for the real intake-time model call: one candidate fact
      // per non-trivial line, so the review list has something to act on.
      const pending = devRead<import('@/types').BankItem[]>(DEV_HARVEST_PENDING_KEY, []);
      const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 12).slice(0, 3);
      const created = lines.map((line, i) => ({
        id: `harvest-${Date.now()}-${i}`,
        kind: 'fact' as const,
        category: null,
        text: line,
        status: 'unconfirmed' as const,
      }));
      devWrite(DEV_HARVEST_PENDING_KEY, [...pending, ...created]);
      return { created: created.length, items: created, skipped_duplicates: 0 };
    }
    const response = await this.client.post(`/workspaces/${workspaceId}/harvest/paste`, { text });
    return response.data;
  }

  async harvestResearch(workspaceId: string): Promise<import('@/types').HarvestResult> {
    if (DEV_MODE) {
      await delay(600);
      return { created: 0, items: [], skipped_duplicates: 0 };
    }
    const response = await this.client.post(`/workspaces/${workspaceId}/harvest/research`);
    return response.data;
  }

  async confirmHarvestItem(workspaceId: string, itemId: string): Promise<void> {
    if (DEV_MODE) {
      await delay(150);
      const pending = devRead<import('@/types').BankItem[]>(DEV_HARVEST_PENDING_KEY, []);
      devWrite(DEV_HARVEST_PENDING_KEY, pending.filter(i => i.id !== itemId));
      return;
    }
    await this.client.post(`/workspaces/${workspaceId}/harvest/${itemId}/confirm`);
  }

  async rejectHarvestItem(workspaceId: string, itemId: string): Promise<void> {
    if (DEV_MODE) {
      await delay(150);
      const pending = devRead<import('@/types').BankItem[]>(DEV_HARVEST_PENDING_KEY, []);
      devWrite(DEV_HARVEST_PENDING_KEY, pending.filter(i => i.id !== itemId));
      return;
    }
    await this.client.post(`/workspaces/${workspaceId}/harvest/${itemId}/reject`);
  }

  // ============================================
  // Content Briefs
  // ============================================

  async getBriefs(workspaceId?: string): Promise<import('@/types').WorkspaceBriefs> {
    if (DEV_MODE) {
      await delay(150);
      return { workspace_id: workspaceId || 'ws-dev-1', monthly: null, weekly: null };
    }
    const params = workspaceId ? { workspace_id: workspaceId } : {};
    const response = await this.client.get('/briefs', { params });
    return response.data;
  }

  async refreshBriefs(workspaceId?: string, cadence?: 'monthly' | 'weekly'): Promise<import('@/types').WorkspaceBriefs> {
    if (DEV_MODE) {
      await delay(300);
      return { workspace_id: workspaceId || 'ws-dev-1', monthly: null, weekly: null };
    }
    const params: Record<string, string> = {};
    if (workspaceId) params.workspace_id = workspaceId;
    if (cadence) params.cadence = cadence;
    const response = await this.client.post('/briefs/refresh', null, { params });
    return response.data;
  }

  // ============================================
  // Analytics — Import & Review (Sprint 23)
  // ============================================

  async uploadAnalyticsFile(
    workspaceId: string,
    file: File,
    platform: import('@/types').AnalyticsPlatform
  ): Promise<import('@/types').BatchDetail | import('@/types').NeedsMappingResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('platform', platform);
    const response = await this.client.post(
      `/workspaces/${workspaceId}/analytics/imports/file`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  }

  async confirmAnalyticsMapping(
    workspaceId: string,
    data: import('@/types').ConfirmMappingRequest
  ): Promise<import('@/types').BatchDetail> {
    const response = await this.client.post(
      `/workspaces/${workspaceId}/analytics/imports/file/confirm-mapping`,
      data
    );
    return response.data;
  }

  async uploadAnalyticsScreenshot(
    workspaceId: string,
    file: File,
    platform: import('@/types').AnalyticsPlatform
  ): Promise<import('@/types').BatchDetail> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('platform', platform);
    const response = await this.client.post(
      `/workspaces/${workspaceId}/analytics/imports/screenshot`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  }

  async getAnalyticsBatches(workspaceId: string): Promise<import('@/types').BatchSummary[]> {
    const response = await this.client.get(`/workspaces/${workspaceId}/analytics/imports`);
    return response.data;
  }

  async getAnalyticsBatch(workspaceId: string, batchId: string): Promise<import('@/types').BatchDetail> {
    const response = await this.client.get(`/workspaces/${workspaceId}/analytics/imports/${batchId}`);
    return response.data;
  }

  async updateAnalyticsRow(
    workspaceId: string,
    batchId: string,
    rowId: string,
    data: import('@/types').RowUpdate
  ): Promise<import('@/types').MetricRow> {
    const response = await this.client.patch(
      `/workspaces/${workspaceId}/analytics/imports/${batchId}/rows/${rowId}`,
      data
    );
    return response.data;
  }

  async deleteAnalyticsRow(workspaceId: string, batchId: string, rowId: string): Promise<void> {
    await this.client.delete(`/workspaces/${workspaceId}/analytics/imports/${batchId}/rows/${rowId}`);
  }

  async confirmAnalyticsBatch(
    workspaceId: string,
    batchId: string,
    timezone: string
  ): Promise<import('@/types').BatchDetail> {
    const response = await this.client.post(
      `/workspaces/${workspaceId}/analytics/imports/${batchId}/confirm`,
      { timezone }
    );
    return response.data;
  }

  async discardAnalyticsBatch(workspaceId: string, batchId: string): Promise<import('@/types').BatchSummary> {
    const response = await this.client.post(`/workspaces/${workspaceId}/analytics/imports/${batchId}/discard`);
    return response.data;
  }
}

export const api = new ApiService();
export default api;

// Standalone Dashboard / Calendar exports
// (for use in dashboard components)
// ============================================

export const getDashboardStats = () => api.getDashboardStats();
export const getCalendarNotes = (year: number, month: number) => api.getCalendarNotes(year, month);
export const upsertCalendarNote = (date: string, content: string) => api.upsertCalendarNote(date, content);
export const deleteCalendarNote = (date: string) => api.deleteCalendarNote(date);
export const getCampaignCalendarEntries = (year: number, month: number, workspaceId?: string) =>
  api.getCampaignCalendarEntries(year, month, workspaceId);
